import {SocketClient} from "cbcore-ts"
import {IF, nil, NO, UIRoute, UITextView, UITooltip, UIView, UIViewController, YES} from "uicore-ts"
import {RHProcurement, RHProcurementCopySource, RHProcurementPart} from "../../../models/ProcurementModels"
import {RHCore} from "../Core/RHCore"
import {RHContractingAuthorityRules} from "../ContractingAuthorityRules"
import {RHFormSessionSnapshotStore} from "../Core/RHFormSessionSnapshotStore"
import {CBButtonGroup} from "../Custom components/CB/CBButtonGroup"
import {CBDialogViewShower} from "../Custom components/CB/CBDialogViewShower"
import {RHFocusManager} from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import {RHNativeFocusItem} from "../Custom components/RH/Keyboard navigation/RHNativeFocusItem"
import {RHPageFocusManager} from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import {RHAlertBannerView} from "../Custom components/RH/RHAlertBannerView"
import {RHButton} from "../Custom components/RH/RHButton"
import type {RHBreadcrumbItem} from "../Custom components/RH/RHBreadcrumbView"
import {RHCheckbox} from "../Custom components/RH/RHCheckbox"
import {RHColor} from "../Custom components/RH/RHColor"
import {RHDatePicker} from "../Custom components/RH/RHDatePicker"
import {RHDropdown} from "../Custom components/RH/RHDropdown"
import {RHFileUploadView, UploadedFile} from "../Custom components/RH/RHFileUploadView"
import {RHFloatingMenuItem, RHFloatingMenuView} from "../Custom components/RH/RHFloatingMenuView"
import {RHPanelView} from "../Custom components/RH/RHPanelView"
import {RHRadioButton} from "../Custom components/RH/RHRadioButton"
import {RHTextArea} from "../Custom components/RH/RHTextArea"
import {RHTextField} from "../Custom components/RH/RHTextField"
import {RHTeamUserReferenceResolver} from "../Custom components/RH/RHTeamRoleSectionView"
import type {RHValidationIssue} from "../Custom components/RH/RHValidationIssuesDialogView"
import {RHValidationIssuesDialogView} from "../Custom components/RH/RHValidationIssuesDialogView"
import {RHCPVCodeFormatter} from "../Custom components/RH/Utils_slop/RHCPVCodeFormatter"
import {RHButtons, RHColors, RHFont, RHForms, RHSpacing} from "../Custom components/RH/Utils_slop/RHDesignTokens"
import {RHClipboard} from "../Custom components/RH/Utils_slop/RHClipboard"
import {RHTextSanitizer} from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import {RHNavigationResettable} from "../Custom components/RH/Utils_slop/RHNavigationResettable"
import {RHProcurementPartDisplay} from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import {
    RHNewProcurementViewTokens,
    RHProcurementWorkflowTitleTokens
} from "../Custom components/RH/Utils_slop/RHViewTokens"
import {ProcurementDetailsViewController} from "../ProcurementDetails/ProcurementDetailsViewController"
import {ProcurementRoundContext} from "../ProcurementDetails/ProcurementRoundContext"
import {ProcurementExcelExportDownloader} from "../ProcurementExcelExport/ProcurementExcelExportDownloader"
import {
    RHAmendPublishedProcurementInput,
    RHContractingAuthorityType,
    RHCPVSummationReport,
    RHCPVSummationReportRequest,
    RHIncomingOffersContentSnapshot,
    RHProcurementAmendmentPreview,
    RHProcurementCapabilities,
    RHProcurementPublicationVisibility,
    RHProcurementSourceSelectionMode,
    RHProcurementSourceSelectionResult,
    RHProcurementTemplateLockCategory,
    RHProcurementTemplateLocks,
    RHRegistryProcurementLot,
    RHRegistryProcurement,
    WithNullsForOptional
} from "../SocketClientFunctions"
import {UserMainViewController} from "../UserMain/UserMainViewController"
import {ProcurementSearchViewController} from "../Search/ProcurementSearchViewController"
import {CopyProcurementDialogView} from "./CopyProcurementDialogView"
import {ContractingAuthorityChoicesView} from "./ContractingAuthorityChoicesView"
import {CostSummaryView} from "./CostSummaryView"
import {CPVSummationTemplateReportView} from "./CPVSummation/CPVSummationTemplateReportView"
import type {CPVSummationRowInfo} from "./CPVTreeDialogView"
import {NewProcurementPreviewViewController} from "./NewProcurementPreviewViewController"
import {ProcurementPartEditorView} from "./ProcurementPartEditorView"
import {ProcurementPartsEditorView} from "./ProcurementPartsEditorView"
import {ProcurementTeamView, RHTeamRoleMatrixView} from "./ProcurementTeamView"
import {ProcurementTemplateLockButton, type ProcurementTemplateLockControlConfiguration} from "./ProcurementTemplateLockButton"
import {RequiredDocumentsEditorSessionSnapshot, RequiredDocumentsEditorView} from "./RequiredDocumentsEditorView"
import { populatedValueOfReference } from "../Core/RHReference"
import { messageOfError } from "../Core/RHErrorMessage"
import { referenceId } from "../Core/RHReference"
import { withoutNullFields } from "../Core/RHNullFields"


type NewProcurementFormSessionSnapshot = {
    procurement: Parameters<typeof SocketClient.AddOrModifyNewProcurement>[0]
    uploadedFiles: UploadedFile[]
    requiredDocuments: RequiredDocumentsEditorSessionSnapshot
    controls: {
        autoExtendSelected: boolean
        lessThanOffersText: string
        extendDeadlineDaysText: string
        allowMultipleOffersPerPartSelected: boolean
    }
    basis: {
        companyId: string
        mode: "blank" | "copy" | "template" | "edit-template" | "edit-draft"
        sourceId?: string
        sourceTemplateId?: string
        sourceTemplateTitle?: string
        shouldShowTeamFromSourceTemplate?: boolean
        sourceTemplateLocks?: RHProcurementTemplateLocks
        templateLocks?: RHProcurementTemplateLocks
        expectedTemplateUpdatedAt?: string
        templateInstantiationKey?: string
    }
}


// What the form is filled from: a stored procurement, or an unsaved draft that has no company, tenderers or primary user yet.
type RHProcurementFormSource =
    Omit<RHProcurement, "procuringCompany" | "registeredTenderers" | "primaryProcuringUser"> &
    Partial<Pick<RHProcurement, "procuringCompany" | "registeredTenderers" | "primaryProcuringUser">>


export class NewProcurementViewController extends UIViewController implements RHNavigationResettable {


    static readonly _publicPublicationModeExplanation =
        "Avalik hange on nähtav hangete otsingus ning sellega saavad tutvuda ja pakkumuse esitada kõik huvitatud ettevõtjad."
    static readonly _directedPublicationModeExplanation =
        "Mitteavalik hange ei ole hangete otsingus nähtav ja teised ettevõtjad seda ei näe. Pakkumuse esitamise ettepanek saadetakse ainult sinu valitud ettevõtjatele."
    static readonly _publicationModeExplanation =
        NewProcurementViewController._publicPublicationModeExplanation + "\n\n" +
        NewProcurementViewController._directedPublicationModeExplanation
    static readonly _publicationModeInformationIconMarkup =
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" ' +
        `fill="none" style="color:${RHColors.primaryBlue.stringValue};vertical-align:middle;margin-left:6px" ` +
        'aria-hidden="true" focusable="false">' +
        '<circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5"/>' +
        '<path d="M8 7.25V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '<circle cx="8" cy="4.75" r="0.85" fill="currentColor"/>' +
        '</svg>'

    //#region Defining the views

    backdropView = new RHPanelView().addedAsSubviewToView(this.view)


    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header4
    ).configuredWithObject({
        text: "Uus hange",
        textColor: RHColors.textPrimary
    }).addedAsSubviewToView(this.view)


    readonly sourceTemplateExplanationLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            textColor: RHColors.textMuted,
            isSingleLine: NO,
            style: { fontSize: RHFont.sizeSmall, whiteSpace: "normal", overflowWrap: "anywhere" }
        })


    copyPreviousProcurementButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Kopeeri varasem hange",
            style: {
                fontSize: RHFont.sizeSmall
            }
        },
        variant: "brandOrange",
        contentPadding: RHSpacing.small
    })

    startFromTemplateButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Alusta mallist", style: { fontSize: RHFont.sizeSmall } },
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHSpacing.small
    })

    sourceTemplateActionsButton = new RHButton().configuredWithObject({
        titleLabel: {
            text: "Tegevused ▾",
            style: { fontSize: RHNewProcurementViewTokens.templateSourceActions.fontSize }
        },
        variant: "brandOrange",
        contentPadding: RHNewProcurementViewTokens.templateSourceActions.contentPadding
    })
    sourceTemplateActionsMenu = new RHFloatingMenuView({ hasTail: NO })

    // The copy shortcut is dual-state: "Kopeeri varasem hange" opens the dialog;
    // once a previous procurement has filled the form it becomes "Lähtesta vorm",
    // which hard-resets the whole form and reverts the button.
    _copyButtonShowsClear = NO

    readonly templateNameLabel = new UITextView().configuredWithObject({
        innerHTML: `Malli nimetus <span style="color: ${RHColor.danger.stringValue}">*</span>`,
        style: { fontWeight: RHNewProcurementViewTokens.fieldLabelFontWeight }
    })
    readonly templateNameTextField = new RHTextField().configuredWithObject({
        placeholderText: "Sisesta malli nimetus",
        isRequired: YES,
        controlEventTargetAccumulator: { TextChange: () => this._markDirty() }
    })

    readonly procuringCompanyLabel = new UITextView().configuredWithObject({
        text: "Hankija",
        style: { fontWeight: RHNewProcurementViewTokens.fieldLabelFontWeight }
    })

    readonly procuringCompanyDropdown = new RHDropdown<string>().configuredWithObject({
        placeholderText: "Vali hankija",
        controlEventTargetAccumulator: {
            SelectionDidChange: () => {
                const selectedProcuringCompanyId = this.procuringCompanyDropdown.selectedValue
                const companyAffiliationStore = RHCore.sharedInstance.companyAffiliationStore
                if (!selectedProcuringCompanyId || selectedProcuringCompanyId === companyAffiliationStore.activeCompanyId) {
                    return
                }
                companyAffiliationStore.deactivateDefaultAllCompaniesScope()
                companyAffiliationStore.activateCompany(selectedProcuringCompanyId)
            }
        }
    })

    readonly procuringCompanyNameLabel = new UITextView().configuredWithObject({
        isSingleLine: NO
    })

    procurementTitleLabel = new UITextView().addedAsSubviewToView(this.view)
        .configuredWithObject({
            innerHTML: "Hanke nimetus <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>",
            style: {
                fontWeight: RHNewProcurementViewTokens.fieldLabelFontWeight
            }
        })
    procurementTitleTextField = new RHTextField().addedAsSubviewToView(this.view)
        .configuredWithObject({
            placeholderText: "Sisesta hanke nimetus",
            isRequired: YES
        })
    procurementTitleValidationMessageLabel = new UITextView()
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColor.danger
        })

    contractingAuthorityTitleLabel = new UITextView().addedAsSubviewToView(this.view)
        .configuredWithObject({
            innerHTML: "Hankija määratlus <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>",
            style: {
                fontWeight: RHNewProcurementViewTokens.fieldLabelFontWeight
            }
        })

    readonly contractingAuthorityChoicesView = new ContractingAuthorityChoicesView()
        .addedAsSubviewToView(this.view)

    contractingAuthorityButtons = this.contractingAuthorityChoicesView.options

    contractingAuthorityButtonGroup = new CBButtonGroup(Object.fromEntries(
        this.contractingAuthorityButtons.map(option => [option.authorityType, option.button])
    )).configuredWithObject({
        selectionDidChange: () => {
            this._markDirty()
            this._scheduleCPVSummationReport()
        }
    })

    readonly cpvSummationErrorLabel = new UITextView().configuredWithObject({
        textColor: RHColor.danger,
        isSingleLine: NO
    })

    readonly templateContractReportView = new CPVSummationTemplateReportView().configuredWithObject({
        exportButton: { controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => this._exportTemplateUsageForId(this._templateIdForContractReport)
        } }
    })
    _isTemplateContractExportAllowed = NO

    registryReferenceLabel = new UITextView().configuredWithObject({
        text: "Riigihangete registri viitenumber",
        style: { fontWeight: RHNewProcurementViewTokens.fieldLabelFontWeight }
    }).addedAsSubviewToView(this.view)
    registryReferenceTextField = new RHTextField().configuredWithObject({
        placeholderText: "Viitenumber, kui mall kuulub registris sõlmitud raamlepingu või hankesüsteemi juurde"
    }).addedAsSubviewToView(this.view)
    readonly registryLoadingView = new UIView()
    readonly registryTitleBanner = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Success,
        padding: RHSpacing.small
    })
    registryPartLabel = new UITextView().configuredWithObject({
        text: "Riigihangete registri osanumber *",
        style: { fontWeight: RHNewProcurementViewTokens.fieldLabelFontWeight }
    }).addedAsSubviewToView(this.view)
    registryPartTextField = new RHTextField().configuredWithObject({
        placeholderText: "Osanumber, kui see on olemas"
    }).addedAsSubviewToView(this.view)
    registryLotDropdown = new RHDropdown<string>().configuredWithObject({
        placeholderText: "Vali riigihangete registri osa"
    })
    readonly registryPartSelectionBanner = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Warning,
        padding: RHSpacing.small
    })
    readonly registryPartTitleBanner = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Success,
        padding: RHSpacing.small
    })
    _pendingRegistryLots: RHRegistryProcurementLot[] = []
    _registryProcurement?: RHRegistryProcurement
    _registryLookupReferenceNumber?: string
    _registryLookupPromise?: Promise<void>
    _registryLookupMessage = ""

    publicationModeTitleLabel = new UITextView().addedAsSubviewToView(this.view)
        .configuredWithObject({
            text: "Avaldamise viis" + NewProcurementViewController._publicationModeInformationIconMarkup,
            hoverText: "",
            style: {
                fontWeight: RHNewProcurementViewTokens.fieldLabelFontWeight
            }
        })

    publicProcurementModeButton = new RHRadioButton().addedAsSubviewToView(this.view)
        .configuredWithObject({
            titleLabel: { text: "Avalik hange" }
        })

    directedProcurementModeButton = new RHRadioButton().addedAsSubviewToView(this.view)
        .configuredWithObject({
            titleLabel: { text: "Mitteavalik hange" }
        })

    publicationModeButtonGroup = new CBButtonGroup({
        "public": this.publicProcurementModeButton,
        "directed": this.directedProcurementModeButton
    }).configuredWithObject({
        value: "public",
        selectionDidChange: () => {
            this._updateInvitedCompaniesButtonVariant()
            if (this._selectedPublicationVisibility === "directed") {
                for (const editor of this.partsEditorView._partEditorViews) {
                    editor.openInvitedCompaniesButton.startBorderPulseAnimationWithColor(RHColors.successGreenSurfaceHover)
                }
            }
            this._markDirty()
            this.view.setNeedsLayoutUpToRootView()
        }
    })


    submissionDeadlineTitleLabel = new UITextView().addedAsSubviewToView(this.view)
        .configuredWithObject({
            innerHTML: "Pakkumuste esitamise tähtaeg <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>"
        })
    submissionDeadlineDatePicker = new RHDatePicker().configuredWithObject({
        type: "datetime",
        minDate: new Date(),
        placeholder: "Vali kuupäev ja kellaaeg",
        isRequired: YES
    }).addedAsSubviewToView(this.view)
    submissionDeadlineValidationMessageLabel = new UITextView()
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColor.danger
        })


    autoExtendProcurementCheckbox = new RHCheckbox().addedAsSubviewToView(this.view)
        .configuredWithObject({
            titleLabel: { text: "Kui pakkumusi on alla" },
            selected: NO,
            controlEventTargetAccumulator: {
                SelectionChange: (sender, event) => {

                    this.lessThanOffersTextField.enabled = this.autoExtendProcurementCheckbox.selected
                    this.extendDeadlineDaysTextField.enabled = this.autoExtendProcurementCheckbox.selected

                    this.lessThanOffersTextField.validate()
                    this.extendDeadlineDaysTextField.validate()
                    this._markDirty()

                }
            }
        })
    lessThanOffersTextField = new RHTextField().addedAsSubviewToView(this.view)
        .configuredWithObject({
            type: "number",
            minValue: 1,
            validationRegex: new RegExp("^[1-9]\\d*$"),
            isRequired: YES,
            placeholderText: "",
            enabled: NO
        })
    autoExtendProcurementMiddleLabel = new UITextView().addedAsSubviewToView(this.view)
        .configuredWithObject({
            text: ", pikenda hanget"
        })
    extendDeadlineDaysTextField = new RHTextField().addedAsSubviewToView(this.view)
        .configuredWithObject({
            type: "number",
            minValue: 1,
            validationRegex: new RegExp("^[1-9]\\d*$"),
            isRequired: YES,
            placeholderText: "",
            enabled: NO
        })
    autoExtendProcurementEndLabel = new UITextView().addedAsSubviewToView(this.view)
        .configuredWithObject({
            text: "päeva võrra."
        })

    amendmentSectionTitleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Muudatuse selgitus",
            textColor: RHColors.textPrimary
        })

    changeExplanationLabel = new UITextView()
        .configuredWithObject({
            innerHTML: "Selgitus <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>",
            style: {
                fontWeight: RHNewProcurementViewTokens.fieldLabelFontWeight
            }
        })

    changeExplanationTextArea = new RHTextArea()
        .configuredWithObject({
            placeholderText: "Selgita, mida ja miks avaldatud hankes muudetakse.",
            isRequired: YES
        })

    allowMultipleOffersPerPartCheckbox = new RHCheckbox().addedAsSubviewToView(this.view)
        .configuredWithObject({
            titleLabel: { text: "Luba ühel pakkujal esitada mitu pakkumust" },
            selected: NO,
            controlEventTargetAccumulator: {
                SelectionChange: () => {
                    this._markDirty()
                }
            }
        })

    _hasMultipleSubmittedOffersPerPart = NO

    multipleOffersLockExplanationView = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Warning,
            text: "Mitme pakkumuse lubamist ei saa välja lülitada, sest samalt pakkujalt on hankeosale juba esitatud mitu pakkumust."
        })

    // File Upload Section
    fileUploadSectionTitleLabel = new UITextView(
        undefined,
        UITextView.type.header4
    ).configuredWithObject({
        text: "Hankedokumendid",
        textColor: RHColors.textPrimary
    }).addedAsSubviewToView(this.view)

    fileUploadView = new RHFileUploadView()
        .configuredWithObject({
            allowedFileTypes: RHFileUploadView.allowedFileTypesForProcurementDocuments
        })
        .addedAsSubviewToView(this.view)


    // ===== PARTS SECTION =====
    partsSectionTitleLabel = new UITextView(
        undefined,
        UITextView.type.header4
    ).configuredWithObject({
        text: "Hankeosad",
        textColor: RHColors.textPrimary
    }).addedAsSubviewToView(this.view)

    partsSectionExplanationLabel = new UITextView().addedAsSubviewToView(this.view)
        .configuredWithObject({
            text: "Iga hange koosneb vähemalt ühest osast. Saad lisada mitu osa, kui soovid jaotada hanke erinevateks komponentideks."
        })


    partsEditorView = new ProcurementPartsEditorView().addedAsSubviewToView(this.view)

    costSummaryView = new CostSummaryView().addedAsSubviewToView(this.view)

    publishEstimatedCostTitleLabel = new UITextView().addedAsSubviewToView(this.view)
        .configuredWithObject({
            innerHTML: "Avalda eeldatav kogumaksumus pakkujatele <span style=\"color: " +
                RHColor.danger.stringValue + "\">*</span>",
            style: {
                fontWeight: RHNewProcurementViewTokens.fieldLabelFontWeight
            }
        })

    publishEstimatedCostYesButton = new RHRadioButton().addedAsSubviewToView(this.view)
        .configuredWithObject({ titleLabel: { text: "Jah" } })

    publishEstimatedCostNoButton = new RHRadioButton().addedAsSubviewToView(this.view)
        .configuredWithObject({ titleLabel: { text: "Ei" } })

    readonly publishEstimatedCostValidationMessage =
        "Vali, kas eeldatav kogumaksumus avaldatakse pakkujatele."

    publishEstimatedCostValidationMessageLabel = new UITextView()
        .configuredWithObject({
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColor.danger
        })

    publishEstimatedCostButtonGroup = new CBButtonGroup({
        "yes": this.publishEstimatedCostYesButton,
        "no": this.publishEstimatedCostNoButton
    }).configuredWithObject({
        selectionDidChange: () => {
            this._updatePublishEstimatedCostValidationMessageVisibility(NO)
            this._markDirty()
        }
    })

    // ===== REQUIRED DOCUMENTS SECTION =====
    requiredDocumentsEditorView = new RequiredDocumentsEditorView().addedAsSubviewToView(this.view)

    procurementTeamView = new ProcurementTeamView().addedAsSubviewToView(this.view)

    readonly templateManagersView = new RHTeamRoleMatrixView().configuredWithObject({
        addsCurrentUserWhenMissing: NO,
        titleLabel: { text: "Malli haldajad" },
        explanationLabel: {
            text: "Malli looja on automaatselt haldaja ja teda ei saa eemaldada. Vali teised liikmed, kellel on lubatud seda malli muuta. Ettevõtte administraatorid saavad malli alati hallata. Malli haldajaid ei lisata mallist loodava hanke meeskonda."
        },
        emptyStateBanner: { text: "Ettevõttes ei ole aktiivseid kasutajaid, keda malli haldajaks valida." },
        teamDidChange: () => this._markDirty()
    })

    readonly showTeamInDerivedProcurementsCheckbox = new RHCheckbox().configuredWithObject({
        titleLabel: { text: "Näita hankemeeskonda malli kasutamisel" },
        selected: YES,
        controlEventTargetAccumulator: { SelectionChange: () => this._markDirty() }
    })

    requiredFieldsLegendLabel = new UITextView().addedAsSubviewToView(this.view)
        .configuredWithObject({
            innerHTML: "<span style=\"color: " + RHColor.danger.stringValue + "\">*</span> Kohustuslikud väljad",
            style: {
                fontSize: "0.85em"
            }
        })

    saveButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Salvesta" },
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHButtons.compactPrimaryContentPadding,
        style: { fontSize: RHButtons.compactPrimaryFontSize },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async () => {
                if (!this.saveButton.enabled) {
                    return
                }
                this.saveButton.enabled = NO
                this.nextButton.enabled = NO
                try {
                    const savedProcurement = await this.saveDraft({ allowsIncompleteForm: YES })
                    if (!savedProcurement) {
                        return
                    }
                    CBDialogViewShower.alert("Hange salvestatud", () => {
                        const routeComponentNameToRemove =
                            this.hostRouteComponentName ?? NewProcurementViewController.routeComponentName
                        UIRoute.currentRoute
                            .routeByRemovingComponentNamed(routeComponentNameToRemove)
                            .routeWithComponent(UserMainViewController.routeComponentName, {
                                [UserMainViewController.ParameterIdentifierName.tab]: "drafts",
                                [UserMainViewController.ParameterIdentifierName.draftCategory]: "draft"
                            })
                            .apply()
                    })
                }
                finally {
                    this.saveButton.enabled = YES
                    this.nextButton.enabled = YES
                }
            }
        }
    })

    nextButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Hanke eelvaade \u2192"
        },
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHButtons.compactPrimaryContentPadding,
        style: {
            fontSize: RHButtons.compactPrimaryFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async (sender, event) => {

                if (this._isPublishedAmendmentMode) {
                    await this._savePublishedAmendment()
                    return
                }

                if (this._mode === "template") {
                    // Templates never reach the preview/publish flow — save,
                    // then return to whichever tab hosted the "Loo uus mall"
                    // entry point (normally "Ettevalmistamisel ja mallid").
                    const savedTemplate = await this.saveDraft()
                    if (savedTemplate) {
                        CBDialogViewShower.alert("Sinu mall on salvestatud.", () => {
                            const routeComponentNameToRemove =
                                this.hostRouteComponentName ?? NewProcurementViewController.routeComponentName
                            UIRoute.currentRoute
                                .routeByRemovingComponentNamed(routeComponentNameToRemove)
                                .routeWithComponent(UserMainViewController.routeComponentName, {})
                                .apply()
                        })
                    }
                    return
                }

                const updatedProcurement = await this.saveDraft()

                if (updatedProcurement) {

                    const routeComponentNameToRemove =
                        this.hostRouteComponentName ?? NewProcurementViewController.routeComponentName
                    const backToRouteComponentName = this.backToRouteComponentName
                        ?? (UIRoute.currentRoute.componentWithViewController(NewProcurementViewController)
                            ?.parameters.backTo as string | undefined)
                        ?? UserMainViewController.routeComponentName
                    const previewParameters: Record<string, string> = { id: (updatedProcurement._id ?? "") + "" }
                    if (backToRouteComponentName !== UserMainViewController.routeComponentName) {
                        previewParameters[NewProcurementPreviewViewController.ParameterIdentifierName.backTo] =
                            backToRouteComponentName
                    }
                    UIRoute.currentRoute
                        .routeByRemovingComponentNamed(routeComponentNameToRemove)
                        .routeWithViewControllerComponent(NewProcurementPreviewViewController, previewParameters)
                        .apply()

                }

            }
        }
    })

    cancelAmendmentButton = new RHButton().configuredWithObject({
        titleLabel: {
            text: "Tühista"
        },
        variant: RHButtons.exitUtilityVariant,
        contentPadding: RHButtons.compactPrimaryContentPadding,
        style: {
            fontSize: RHButtons.compactPrimaryFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._routeBackToProcurementDetails()
            }
        }
    })

    footerClearanceView = new UIView().addedAsSubviewToView(this.view)

    //#endregion Defining the views

    static procurement?: WithNullsForOptional<Omit<Omit<RHProcurement, "procuringCompany">, "primaryProcuringUser">>

    readonly _templateLockButtons = {
        title: new ProcurementTemplateLockButton("Hanke nimetus"),
        contractingAuthorityType: new ProcurementTemplateLockButton("Hankija määratlus"),
        publicationVisibility: new ProcurementTemplateLockButton("Avaldamise viis"),
        parts: new ProcurementTemplateLockButton("Hankeosad"),
        publishEstimatedCost: new ProcurementTemplateLockButton("Eeldatava maksumuse avaldamine"),
        maxOffersPerPart: new ProcurementTemplateLockButton("Mitu pakkumust"),
        deadlineDate: new ProcurementTemplateLockButton("Pakkumuste esitamise tähtaeg"),
        deadlineExtension: new ProcurementTemplateLockButton("Tähtaja automaatne pikendamine"),
        files: new ProcurementTemplateLockButton("Hankedokumendid")
    }

    _isDirty = NO
    _roundAmendmentIdentity?: { rootId: string, title: string, label: string }
    _didValidateFromRoute = NO
    _didApplyCopySourceFromRoute = NO

    // "template" when composing/editing a company-shared template rather than
    // an ordinary procurement draft — set from the templateMode route
    // parameter (DraftProcurementsViewController's "Loo uus mall" entry
    // point) or detected from a loaded procurement's canonical status (see
    // _loadProcurementIfPossible). Drives _updateViewsForFormMode's title/button label swap.
    _mode: "procurement" | "template" = "procurement"
    _basisMode: "blank" | "copy" | "template" | "edit-template" | "edit-draft" = "blank"
    _basisGeneration = 0
    _basisCompanyId?: string
    _sourceTemplateId?: string
    _sourceTemplateTitle?: string
    _basisSourceId?: string
    _sourceTemplateLocks?: RHProcurementTemplateLocks
    _templateLocks: RHProcurementTemplateLocks = {
        isCpvLocked: NO,
        isRequiredDocumentsLocked: NO,
        isInvitedCompaniesLocked: NO
    }
    _expectedTemplateUpdatedAt?: string
    _shouldShowTeamFromSourceTemplate = YES
    _templateInstantiationKey?: string
    _templateCapabilities?: RHProcurementCapabilities
    _sourceTemplateActionIsPending = NO
    _formHydrationPromise?: Promise<void>
    _detachedFormSubviews: UIView[] = []
    _formAppearanceGeneration = 0
    _formAppearanceIsActive = NO
    _allowsFormSessionRestoreAfterLoad = YES
    _formStateIsBeingApplied = NO
    private _cpvSummationReportRefreshTimer?: ReturnType<typeof setTimeout>
    private _cpvSummationReportRequestSerial = 0
    private _latestCPVSummationReport?: RHCPVSummationReport
    _registryLookupRequestSerial = 0

    override _focusManager = new RHFocusManager(() => {

        const items: (UIView | RHFocusManager | RHNativeFocusItem)[] = []
        if (this._detachedFormSubviews.length) {
            return items
        }
        for (const basisControl of [
            this.copyPreviousProcurementButton,
            this.startFromTemplateButton,
            this.sourceTemplateActionsButton
        ]) {
            if (basisControl.superview === this.view) {
                items.push(basisControl)
            }
        }
        if (this.procuringCompanyDropdown.superview === this.view) {
            const procuringCompanySelectElement = this.procuringCompanyDropdown.viewHTMLElement.querySelector("select")
            if (procuringCompanySelectElement) items.push(new RHNativeFocusItem(procuringCompanySelectElement))
        }
        if (this.templateNameTextField.superview === this.view) {
            items.push(this.templateNameTextField)
        }
        if (!this.registryReferenceTextField.hidden) {
            items.push(this.registryReferenceTextField)
        }
        if (this.registryPartTextField.superview === this.view) {
            items.push(this.registryPartTextField)
        }
        if (this.registryLotDropdown.superview === this.view) {
            items.push(this.registryLotDropdown)
        }
        if (this.templateContractReportView.superview === this.view) {
            items.push(this.templateContractReportView.exportButton)
        }
        if (this.procurementTitleTextField.superview === this.view) {
            items.push(this.procurementTitleTextField)
        }
        if (this._templateLockButtons.title.superview === this.view) {
            items.push(this._templateLockButtons.title, this._templateLockButtons.title.visibilityButton)
        }
        if (this._templateLockButtons.contractingAuthorityType.superview === this.view) {
            items.push(this._templateLockButtons.contractingAuthorityType, this._templateLockButtons.contractingAuthorityType.visibilityButton)
        }
        items.push(...this.contractingAuthorityButtons.filter(option => !option.button.hidden).map(option => option.button))
        if (!this.publicationModeTitleLabel.hidden) {
            if (this._templateLockButtons.publicationVisibility.superview === this.view) {
                items.push(this._templateLockButtons.publicationVisibility, this._templateLockButtons.publicationVisibility.visibilityButton)
            }
            items.push(this.publicProcurementModeButton)
            items.push(this.directedProcurementModeButton)
        }

        items.push(this.partsEditorView._focusManager!)

        if (this._templateLockButtons.parts.superview === this.view) {
            items.push(this._templateLockButtons.parts, this._templateLockButtons.parts.visibilityButton)
        }
        items.push(this.partsEditorView.addPartButton)
        if (this._templateLockButtons.publishEstimatedCost.superview === this.view) {
            items.push(this._templateLockButtons.publishEstimatedCost, this._templateLockButtons.publishEstimatedCost.visibilityButton)
        }
        items.push(this.publishEstimatedCostYesButton)
        items.push(this.publishEstimatedCostNoButton)

        if (this.requiredDocumentsEditorView.templateLockButton.superview ===
            this.requiredDocumentsEditorView) {
            items.push(this.requiredDocumentsEditorView.templateLockButton, this.requiredDocumentsEditorView.templateLockButton.visibilityButton)
        }

        // Required document rows: remove button, name, description, then part checkboxes
        this.requiredDocumentsEditorView._rows.forEach(row => {
            items.push(row.removeButton)
            items.push(row.nameTextField)
            items.push(row.descriptionEditor)
            row._partCheckboxes.forEach(checkbox => items.push(checkbox))
        })

        items.push(this.requiredDocumentsEditorView.addDocumentButton)
        if (this._templateLockButtons.maxOffersPerPart.superview === this.view) {
            items.push(this._templateLockButtons.maxOffersPerPart, this._templateLockButtons.maxOffersPerPart.visibilityButton)
        }
        items.push(this.allowMultipleOffersPerPartCheckbox)

        if (this._templateLockButtons.deadlineDate.superview === this.view) {
            items.push(this._templateLockButtons.deadlineDate, this._templateLockButtons.deadlineDate.visibilityButton)
        }
        items.push(this.submissionDeadlineDatePicker)
        if (this._templateLockButtons.deadlineExtension.superview === this.view) {
            items.push(this._templateLockButtons.deadlineExtension, this._templateLockButtons.deadlineExtension.visibilityButton)
        }
        items.push(this.autoExtendProcurementCheckbox)

        // Auto-extend fields — only when the checkbox is checked (fields are enabled)
        if (this.lessThanOffersTextField.enabled) {
            items.push(this.lessThanOffersTextField)
            items.push(this.extendDeadlineDaysTextField)
        }

        if (this.procurementTeamView.templateLockButton.superview === this.procurementTeamView) {
            items.push(this.procurementTeamView.templateLockButton, this.procurementTeamView.templateLockButton.visibilityButton)
        }
        items.push(this.procurementTeamView._focusManager!)
        if (this.templateManagersView.superview === this.view) {
            items.push(this.templateManagersView._focusManager!, this.showTeamInDerivedProcurementsCheckbox)
        }

        if (this._templateLockButtons.files.superview === this.view) {
            items.push(this._templateLockButtons.files, this._templateLockButtons.files.visibilityButton)
        }
        items.push(this.fileUploadView.dropZone)
        if (this.changeExplanationTextArea.superview === this.view) {
            items.push(this.changeExplanationTextArea)
        }
        if (this.cancelAmendmentButton.superview === this.view) {
            items.push(this.cancelAmendmentButton)
        }
        if (this.saveButton.superview === this.view) {
            items.push(this.saveButton)
        }
        if (this.nextButton.superview === this.view) {
            items.push(this.nextButton)
        }

        return items

    })


    constructor(view: UIView) {

        super(view)

        this.view.configureWithObject({
            backgroundColor: RHColor.backgroundColor
        })

        this.registryLoadingView.viewHTMLElement.setAttribute("role", "status")
        this.registryLoadingView.viewHTMLElement.setAttribute("aria-label", "Laadin riigihanke andmeid")
        this.procuringCompanyDropdown.viewHTMLElement.querySelector("select")?.setAttribute("aria-label", "Hankija")
        UIView.createStyleSelector(
            `#${this.procuringCompanyDropdown.elementID} > select`,
            `border: 2px solid ${RHColors.primaryBlue.stringValue};`
        )

        this.publicationModeTitleLabel.viewHTMLElement.setAttribute(
            "aria-description",
            NewProcurementViewController._publicationModeExplanation
        )
        UITooltip.sharedInstance.attach(
            this.publicationModeTitleLabel,
            NewProcurementViewController._publicationModeExplanation
        )

        // Set up file upload callback
        this.fileUploadView.setOnFilesChanged((files: UploadedFile[]) => {
            console.log("Files changed:", files)
            if (this._isPublishedAmendmentMode && this.class.procurement?.reconciliationParentProcurement) {
                this._applyEditingRestrictionsToForm()
            }
            this._markDirty()
        })

        this.copyPreviousProcurementButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._requestBasisSwitch("previousProcurement")
        }
        this.startFromTemplateButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._requestBasisSwitch("template")
        }
        this.sourceTemplateActionsButton.viewHTMLElement.setAttribute("aria-haspopup", "menu")
        this.sourceTemplateActionsButton.viewHTMLElement.setAttribute("aria-expanded", "false")
        this.sourceTemplateActionsButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.sourceTemplateActionsMenu.presentFromTriggerView(
                this.sourceTemplateActionsButton,
                this._sourceTemplateActionMenuItems()
            )
        }
        this.requiredDocumentsEditorView._onRowAdded = (row) => {
            row.nameTextField.focus()
        }
        this.requiredDocumentsEditorView.documentsDidChange = () => {
            this._markDirty()
        }

        // Wire parts editor callbacks
        this.partsEditorView.cpvSummationInfoForEditorCPVCode = (editor, cpvCode, shouldLoad) =>
            this._summationInfoForEditorCPVCode(editor, cpvCode, shouldLoad)
        this.partsEditorView.cpvTreeSelectionDidChange = () => this._scheduleCPVSummationReport()

        this.partsEditorView.partsDidChange = () => {
            this.updateCostSummary()
            this._updatePartsInRequiredDocumentsEditor()
            this._updatePartTerminology()
            this._scheduleCPVSummationReport()
            this._markDirty()
        }

        this.procurementTitleTextField.controlEventTargetAccumulator.TextChange = () => {
            if (this.procurementTitleValidationMessageLabel.superview === this.view) {
                this._validateProcurementTitle()
            }
            this._markDirty()
        }
        this.lessThanOffersTextField.controlEventTargetAccumulator.TextChange = () => {
            this._markDirty()
        }
        this.registryReferenceTextField.controlEventTargetAccumulator.TextChange = () => {
            const referenceNumber = this.registryReferenceTextField.text.trim()
            if (referenceNumber === this._registryLookupReferenceNumber) return
            this.registryPartTextField.text = ""
            this._resetRegistryLookup()
            if (/^\d{6}$/.test(referenceNumber)) void this._loadRegistryProcurement()
        }
        for (const field of [this.registryReferenceTextField, this.registryPartTextField]) {
            field.controlEventTargetAccumulator.TextChange = () => {
                this._markDirty()
                this._scheduleCPVSummationReport()
            }
        }
        this.registryReferenceTextField.addTargetForControlEvent(UIView.controlEvent.Blur, () => {
            void this._loadRegistryProcurement()
        })
        this.registryLotDropdown.controlEventTargetAccumulator.SelectionDidChange = () => {
            const chosenLot = this._pendingRegistryLots.find(
                lot => lot.partNumber === this.registryLotDropdown.selectedValue
            )
            if (chosenLot) {
                this._applyRegistryLotToForm(chosenLot)
            }
        }
        this.extendDeadlineDaysTextField.controlEventTargetAccumulator.TextChange = () => {
            this._markDirty()
        }
        this.changeExplanationTextArea.controlEventTargetAccumulator.TextChange = () => {
            this._markDirty()
        }

        // Initialize with a single blank part
        this.partsEditorView.procurementParts = []
        this._updatePartsInRequiredDocumentsEditor()
        this._updatePartTerminology()

        this.submissionDeadlineDatePicker.datePickerWillOpen = () => {
            this.submissionDeadlineDatePicker.minDate = new Date()
        }
        this.submissionDeadlineDatePicker.addTargetForControlEvent(
            RHDatePicker.controlEvent.DateChange,
            () => {
                if (this.submissionDeadlineValidationMessageLabel.superview === this.view) {
                    this._validateSubmissionDeadline()
                }
                this._markDirty()
            }
        )

        this.procurementTeamView.teamDidChange = () => {
            this._markDirty()
        }
        this._configureTemplateManagersForTemplate()

        this._updateInvitedCompaniesButtonVariant()
        this._updateViewsForFormMode()

        // Construction and initial population are not user edits.
        this._isDirty = NO

    }


    static override readonly routeComponentName = "new_procurement"
    static override readonly ParameterIdentifierName = {
        "id": "id",
        backTo: "backTo",
        returnProcurementId: "returnProcurementId",
        returnPartId: "returnPartId",
        returnRoundId: "returnRoundId",
        returnTab: "returnTab",
        focusParts: "focusParts",
        mode: "mode",
        validateOnOpen: "validateOnOpen",
        validationMessage: "validationMessage",
        // Canonical template-use/deep-link identifier. It is deliberately
        // distinct from id (edit existing draft) and copySourceId (ordinary copy).
        templateId: "templateId",
        // Set by DraftProcurementsViewController's "Kasuta malli" action —
        // a one-shot instruction to open this tab pre-filled from a copy
        // source (a template or an ordinary draft) instead of blank or
        // editing an existing draft by id. Consumed once via
        // _didApplyCopySourceFromRoute, the same guarded-once pattern
        // validateOnOpen already uses.
        copySourceId: "copySourceId",
        // The copy source's own company — see the comment where this is read
        // in _loadProcurementIfPossible for why it must arrive alongside
        // copySourceId rather than being looked up afterward.
        copySourceCompanyId: "copySourceCompanyId",
        // Set by DraftProcurementsViewController's "Loo uus mall" entry
        // point — opens this tab in template-composition mode instead of an
        // ordinary blank draft. See _mode.
        templateMode: "templateMode"
    }
    static readonly requiredApplicationModes: ("procurer" | "offerer")[] = ["procurer"]

    static _decodedRouteMessage(message: string): string {
        try {
            return decodeURIComponent(message)
        }
        catch (error) {
            return message
        }
    }

    // When this editor is embedded as the "Uus" tab of the procurer dashboard,
    // the dashboard route is the visible content component. Navigation that leaves
    // the editor must therefore remove the dashboard component instead of the
    // standalone new_procurement component.
    hostRouteComponentName?: string
    backToRouteComponentName?: string


    get breadcrumbItems(): RHBreadcrumbItem[] {
        const route = UIRoute.currentRoute
        const baseRoute = route.routeByRemovingComponentsOtherThanOnesNamed(["settings"])
        const parameters = route.componentWithViewController(NewProcurementViewController)?.parameters
        const items: RHBreadcrumbItem[] = []
        if (this._isPublishedAmendmentMode) {
            items.push({
                title: "Otsing",
                route: baseRoute.routeWithViewControllerComponent(ProcurementSearchViewController, {})
            })
            const procurementId = this._roundAmendmentIdentity?.rootId ?? parameters?.returnProcurementId ?? parameters?.id
            if (procurementId) {
                let title = "Hange"
                const procurement = this.class.procurement
                if (procurement?._id?.toString() === procurementId && procurement.referenceNumber) {
                    title += ` ${procurement.referenceNumber}`
                }
                items.push({
                    title,
                    route: baseRoute.routeWithViewControllerComponent(ProcurementDetailsViewController, {
                        id: procurementId, tab: "details"
                    })
                })
            }
        }
        else {
            items.push({
                title: "Töölaud",
                route: baseRoute.routeWithViewControllerComponent(UserMainViewController, { tab: "published" })
            })
            if (this._mode === "template" || parameters?.id || parameters?.templateId || parameters?.copySourceId) {
                items.push({
                    title: "Ettevalmistamisel ja mallid",
                    route: baseRoute.routeWithViewControllerComponent(UserMainViewController, { tab: "drafts" })
                })
            }
        }
        items.push({ title: RHTextSanitizer.plainText(this.titleLabel.text) })
        return items
    }


    _updatePartsInRequiredDocumentsEditor() {
        // Restoring part fields can notify us before the first editor is attached.
        if (this.partsEditorView.partCount === 0) {
            return
        }
        this.requiredDocumentsEditorView.parts = this.partsEditorView.partIdentifiers
    }

    _updatePartTerminology() {
        const partCount = this.partsEditorView.partCount
        let multipleOffersTitle = "Luba ühel pakkujal esitada mitu pakkumust"
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount)) {
            multipleOffersTitle += " osa kohta"
        }
        this.allowMultipleOffersPerPartCheckbox.configureWithObject({
            titleLabel: { text: multipleOffersTitle },
            hoverText: multipleOffersTitle
        })

        if (!this._isTemplateFieldHidden("parts") && (this._mode === "template" ||
            this._sourceTemplateLocks?.isPartsLocked ||
            RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(partCount))) {
            this.partsSectionTitleLabel.addedAsSubviewToView(this.view)
            this.partsSectionExplanationLabel.addedAsSubviewToView(this.view)
        }
        else {
            this.partsSectionTitleLabel.removeFromSuperview()
            this.partsSectionExplanationLabel.removeFromSuperview()
        }

        this.view.setNeedsLayoutUpToRootView()
    }

    /**
     * Recomputes and refreshes the cost summary from current part editor values
     */
    updateCostSummary() {
        this.costSummaryView.update(this.partsEditorView.costEntries)
        this.view.setNeedsLayoutUpToRootView()
    }


    get _selectedPublicationVisibility(): RHProcurementPublicationVisibility {
        if (this.publicationModeButtonGroup.value === "directed") {
            return "directed"
        }
        return "public"
    }


    get _selectedContractingAuthorityType(): RHContractingAuthorityType | undefined {
        return RHContractingAuthorityRules.types.find(authorityType =>
            authorityType === this.contractingAuthorityButtonGroup.value
        )
    }


    _updateContractingAuthorityChoicesWithSelection(selectedType?: RHContractingAuthorityType) {
        const company = RHCore.sharedInstance.companyAffiliationStore.activeCompanyAffiliation?.company ?? {}
        const allowedTypes = RHContractingAuthorityRules.allowedTypesForCompany(company)
        const savedProcurement = this.class.procurement
        if (savedProcurement?._id && selectedType && !allowedTypes.includes(selectedType)) {
            allowedTypes.push(selectedType)
        }
        let selection = ""
        if (selectedType && allowedTypes.includes(selectedType)) {
            selection = selectedType
        }
        else if (allowedTypes.length === 1) {
            selection = allowedTypes[0]
        }
        this.contractingAuthorityButtonGroup.value = selection
        const isChoiceHidden = (allowedTypes.length === 1 && this._mode !== "template") ||
            this._isTemplateFieldHidden("contractingAuthorityType")
        if (isChoiceHidden) {
            this.contractingAuthorityTitleLabel.removeFromSuperview()
            this.contractingAuthorityChoicesView.removeFromSuperview()
            this._templateLockButtons.contractingAuthorityType.removeFromSuperview()
        }
        else {
            this.contractingAuthorityTitleLabel.addedAsSubviewToView(this.view)
            this.contractingAuthorityChoicesView.addedAsSubviewToView(this.view)
        }
        const isLocked = this._isPublishedAmendmentMode || !!savedProcurement?.reconciliationParentProcurement ||
            !!(this._sourceTemplateLocks?.isContractingAuthorityTypeLocked ?? this._sourceTemplateLocks?.isProcurementCategoryLocked)
        for (const option of this.contractingAuthorityButtons) {
            if (allowedTypes.includes(option.authorityType)) {
                option.button.addedAsSubviewToView(this.contractingAuthorityChoicesView)
            }
            else {
                option.button.removeFromSuperview()
            }
            option.button.enabled = !isLocked
        }
        this.contractingAuthorityChoicesView.clearIntrinsicSizeCache()
        this.contractingAuthorityChoicesView.setNeedsLayoutUpToRootView()
    }


    _updateInvitedCompaniesButtonVariant() {
        this.partsEditorView.invitedCompaniesButtonVariant = IF(
            this._selectedPublicationVisibility === "directed"
        )(() => "success" as const).ELSE(() => RHButtons.compactPrimaryVariant)
    }


    _updatePublicationModeFromProcurement(procurement?: Pick<RHProcurement, "publicationVisibility">) {
        if (procurement?.publicationVisibility === "directed") {
            this.publicationModeButtonGroup.value = "directed"
        }
        else {
            this.publicationModeButtonGroup.value = "public"
        }
        this._updateInvitedCompaniesButtonVariant()
    }


    get _isPublishedAmendmentMode(): boolean {
        return UIRoute.currentRoute.componentWithViewController(
            NewProcurementViewController
        )?.parameters.mode === "publishedAmendment"
    }


    _updateViewsForFormMode() {
        if (this._detachedFormSubviews.length) {
            return
        }
        this._updateProcuringCompanyViews()
        this.sourceTemplateExplanationLabel.removeFromSuperview()
        this._updateContractingAuthorityChoicesWithSelection(this._selectedContractingAuthorityType)
        const isAuthoringTemplate = this._mode === "template"
        const hasRegistryReference = !!this.registryReferenceTextField.text.trim()
        for (const view of [this.registryReferenceLabel, this.registryReferenceTextField]) {
            view.hidden = !isAuthoringTemplate && !hasRegistryReference
        }
        this.registryReferenceTextField.enabled = isAuthoringTemplate
        this.registryPartTextField.enabled = NO
        this._updateRegistryViews()
        if (hasRegistryReference && this._registryLookupReferenceNumber !== this.registryReferenceTextField.text.trim()) {
            void this._loadRegistryProcurement()
        }
        this.publicationModeTitleLabel.hidden = this._isPublishedAmendmentMode
        this.publicProcurementModeButton.hidden = this._isPublishedAmendmentMode
        this.directedProcurementModeButton.hidden = this._isPublishedAmendmentMode
        this.saveButton.removeFromSuperview()
        this.nextButton.variant = RHButtons.compactPrimaryVariant
        if (
            !this._isPublishedAmendmentMode &&
            this._mode === "procurement" &&
            !this._sourceTemplateId &&
            this.nextButton.superview === this.view
        ) {
            this.saveButton.addedAsSubviewToView(this.view)
            this.nextButton.variant = "success"
        }
        if (!this._isPublishedAmendmentMode) {
            this.titleLabel.text = "Uus hange"
            this.nextButton.titleLabel.text = "Hanke eelvaade \u2192"
            if (this._sourceTemplateId) {
                this.sourceTemplateExplanationLabel.text =
                    `Alusmall: „${this._sourceTemplateTitle ?? ""}“.`
                this.sourceTemplateExplanationLabel.addedAsSubviewToView(this.view)
                this.nextButton.titleLabel.text = "Kasuta malli"
            }
            if (this._mode === "template") {
                this.sourceTemplateExplanationLabel.removeFromSuperview()
                this.titleLabel.text = "Hankemall"
                this.nextButton.titleLabel.text = "Salvesta mall"
            }
            this._syncBasisAndTemplateLockViews()
            this.amendmentSectionTitleLabel.removeFromSuperview()
            this.changeExplanationLabel.removeFromSuperview()
            this.changeExplanationTextArea.removeFromSuperview()
            this.cancelAmendmentButton.removeFromSuperview()
            return
        }

        this.titleLabel.text = "Muuda avaldatud hanget"
        if (this._roundAmendmentIdentity) {
            this.titleLabel.text = `${this._roundAmendmentIdentity.label} · Muuda andmeid`
        }
        this.nextButton.titleLabel.text = "Salvesta muudatus"
        this.copyPreviousProcurementButton.removeFromSuperview()
        this.startFromTemplateButton.removeFromSuperview()
        this._removeSourceTemplateActions()
        this._applyTemplateAuthoringLockControls()
        this._applyEditingRestrictionsToForm()
        this.amendmentSectionTitleLabel.addedAsSubviewToView(this.view)
        this.changeExplanationLabel.addedAsSubviewToView(this.view)
        this.changeExplanationTextArea.addedAsSubviewToView(this.view)
        this.cancelAmendmentButton.addedAsSubviewToView(this.view)
    }


    _updateProcuringCompanyViews() {
        const companyAffiliationStore = RHCore.sharedInstance.companyAffiliationStore
        const visibleViews: UIView[] = []
        if (this._mode === "procurement" && !this._isPublishedAmendmentMode) {
            visibleViews.push(this.procuringCompanyLabel)
            if (companyAffiliationStore.hasMultipleCompanyAffiliations) {
                this.procuringCompanyDropdown.configureWithObject({
                    options: companyAffiliationStore.affiliations.filter(affiliation =>
                        affiliation.source !== "relationship" && affiliation.membershipStatus === "active"
                    ).flatMap(affiliation => {
                        const affiliatedCompanyId = referenceId(affiliation.company?._id)
                        if (!affiliatedCompanyId) return []
                        const companyOptionLabel = affiliation.company?.name ?? affiliatedCompanyId
                        return [{ label: companyOptionLabel, value: affiliatedCompanyId }]
                    }),
                    selectedValue: companyAffiliationStore.activeCompanyId
                })
                visibleViews.push(this.procuringCompanyDropdown)
            }
            else {
                this.procuringCompanyNameLabel.text = RHTextSanitizer.escapedHTML(
                    companyAffiliationStore.activeCompanyAffiliation?.company?.name ?? ""
                )
                visibleViews.push(this.procuringCompanyNameLabel)
            }
        }
        for (const view of [this.procuringCompanyLabel, this.procuringCompanyDropdown, this.procuringCompanyNameLabel]) {
            if (visibleViews.includes(view)) {
                if (view.superview !== this.view) view.addedAsSubviewToView(this.view)
            }
            else {
                view.removeFromSuperview()
            }
        }
        this.view.setNeedsLayoutUpToRootView()
    }


    _syncBasisAndTemplateLockViews() {
        const usesSourceTemplate = this._mode === "procurement" &&
            this._basisMode === "template" &&
            !!this._sourceTemplateId
        const hasPersistedObject = !!this.class.procurement?._id || !!UIRoute.currentRoute
            .componentWithViewController(NewProcurementViewController)?.parameters.id
        if (usesSourceTemplate) {
            this.copyPreviousProcurementButton.removeFromSuperview()
            this.startFromTemplateButton.removeFromSuperview()
            this._syncSourceTemplateActions()
        }
        else if (hasPersistedObject) {
            this.copyPreviousProcurementButton.removeFromSuperview()
            this.startFromTemplateButton.removeFromSuperview()
            this._removeSourceTemplateActions()
        }
        else {
            this._removeSourceTemplateActions()
            this.copyPreviousProcurementButton.addedAsSubviewToView(this.view)
            if (this._mode === "template") {
                this.startFromTemplateButton.removeFromSuperview()
            }
            else {
                this.startFromTemplateButton.addedAsSubviewToView(this.view)
            }
        }

        if (this._mode === "template") {
            this.submissionDeadlineTitleLabel.text = "Pakkumuste esitamise tähtaeg"
            this.submissionDeadlineDatePicker.isRequired = NO
        }
        else {
            this.submissionDeadlineTitleLabel.innerHTML =
                "Pakkumuste esitamise tähtaeg <span style=\"color: " +
                RHColor.danger.stringValue + "\">*</span>"
            this.submissionDeadlineDatePicker.isRequired = YES
        }
        this._applyTemplateAuthoringLockControls()
        this._applyEditingRestrictionsToForm()
    }


    _syncSourceTemplateActions() {
        if (this._sourceTemplateActionIsPending) {
            this.sourceTemplateActionsMenu.dismiss(NO)
        }
        this.sourceTemplateActionsButton.enabled = !this._sourceTemplateActionIsPending
        this.sourceTemplateActionsButton.addedAsSubviewToView(this.view)
    }


    _sourceTemplateActionMenuItems(): RHFloatingMenuItem[] {
        const capabilities = this._templateCapabilities
        const items: RHFloatingMenuItem[] = [
            {
                title: "Alusta tühjast vormist",
                action: () => this._startFromBlankForm()
            }
        ]
        if (capabilities?.canExportTemplateUsage === YES) {
            items.push({
                title: "Laadi XLSX",
                action: () => this._exportTemplateUsageForId(this._templateIdForContractReport)
            })
        }
        if (capabilities?.canCopyTemplateLink === YES) {
            items.push({
                title: "Kopeeri link",
                action: () => this._copySourceTemplateLink()
            })
        }
        if (capabilities?.canEditTemplate === YES) {
            items.push({
                title: "Muuda",
                action: () => this._editSourceTemplate()
            })
        }
        if (capabilities?.canArchiveTemplate === YES) {
            items.push({
                title: "Arhiveeri",
                action: () => this._confirmArchiveSourceTemplate()
            })
        }
        if (capabilities?.canRestoreTemplate === YES) {
            items.push({
                title: "Taasta",
                action: () => this._runSourceTemplateMutation("restore")
            })
        }
        if (capabilities?.canDeleteTemplate === YES) {
            items.push({
                title: "Kustuta",
                isDestructive: YES,
                action: () => this._confirmDeleteSourceTemplate()
            })
        }
        return items
    }


    _removeSourceTemplateActions() {
        this.sourceTemplateActionsMenu.dismiss(NO)
        this.sourceTemplateActionsButton.removeFromSuperview()
    }


    _currentTemplateLocks(): RHProcurementTemplateLocks {
        const { isProcurementCategoryLocked, ...locks } = this._templateLocks
        return {
            ...locks,
            isContractingAuthorityTypeLocked: locks.isContractingAuthorityTypeLocked ?? isProcurementCategoryLocked ?? NO
        }
    }


    // RHProcurementTemplateLocks fields are the corresponding RHProcurement field name
    // prefixed with "is" and suffixed with "Locked" (e.g. "team" -> "isTeamLocked").
    static _lockFieldNameForCategory(category: RHProcurementTemplateLockCategory): keyof RHProcurementTemplateLocks {
        return `is${category.charAt(0).toUpperCase()}${category.slice(1)}Locked` as keyof RHProcurementTemplateLocks
    }


    _templateLockDidChange(category: RHProcurementTemplateLockCategory, locked: boolean) {
        this._templateLocks = {
            ...this._templateLocks,
            [NewProcurementViewController._lockFieldNameForCategory(category)]: locked
        }
        if (!locked) {
            const hiddenFieldName = NewProcurementViewController._lockFieldNameForCategory(category)
                .replace("Locked", "Hidden") as keyof RHProcurementTemplateLocks
            this._templateLocks[hiddenFieldName] = NO
        }
        this._applyTemplateAuthoringLockControls()
        this._markDirty()
    }


    _templateLockConfigurationForCategory(
        category: RHProcurementTemplateLockCategory
    ): ProcurementTemplateLockControlConfiguration {
        const isAuthoringTemplate = this._mode === "template" && !this._isPublishedAmendmentMode
        const locks = IF(isAuthoringTemplate)(() => this._currentTemplateLocks()).ELSE(() => this._sourceTemplateLocks)
        const lockFieldName = NewProcurementViewController._lockFieldNameForCategory(category)
        const hiddenFieldName = lockFieldName.replace("Locked", "Hidden") as keyof RHProcurementTemplateLocks
        let isLocked = !!locks?.[lockFieldName]
        if (category === "contractingAuthorityType") {
            isLocked = !!(locks?.isContractingAuthorityTypeLocked ?? locks?.isProcurementCategoryLocked)
        }
        return {
            locked: isLocked,
            lockDidChange: value => this._templateLockDidChange(category, value),
            isFieldHidden: isLocked && !!locks?.[hiddenFieldName],
            fieldVisibilityDidChange: IF(isAuthoringTemplate)(() => (isFieldHidden: boolean) => {
                this._templateLocks[hiddenFieldName] = isFieldHidden
                this._applyTemplateAuthoringLockControls()
                this._markDirty()
            }).ELSE(() => undefined)
        }
    }


    _isTemplateFieldHidden(category: RHProcurementTemplateLockCategory): boolean {
        if (this._mode === "template" || this._isPublishedAmendmentMode) {
            return NO
        }
        const lockFieldName = NewProcurementViewController._lockFieldNameForCategory(category)
        const hiddenFieldName = lockFieldName.replace("Locked", "Hidden") as keyof RHProcurementTemplateLocks
        let isLocked = !!this._sourceTemplateLocks?.[lockFieldName]
        if (category === "contractingAuthorityType") {
            isLocked = !!(this._sourceTemplateLocks?.isContractingAuthorityTypeLocked ??
                this._sourceTemplateLocks?.isProcurementCategoryLocked)
        }
        return isLocked && !!this._sourceTemplateLocks?.[hiddenFieldName]
    }


    _applyTemplateAuthoringLockControls() {
        const isAuthoringTemplate = this._mode === "template" && !this._isPublishedAmendmentMode
        const locks = this._currentTemplateLocks()
        this.procurementTitleTextField.isRequired = !isAuthoringTemplate || !!locks.isTitleLocked
        this.submissionDeadlineDatePicker.isRequired = !isAuthoringTemplate || !!locks.isDeadlineDateLocked
        for (const field of [this.lessThanOffersTextField, this.extendDeadlineDaysTextField]) {
            field.isRequired = !isAuthoringTemplate || !!locks.isDeadlineExtensionLocked
            if (!field.isRequired) {
                field.resetValidationVisuals()
            }
        }
        for (const [label, title, isRequired] of [
            [this.procurementTitleLabel, "Hanke nimetus", this.procurementTitleTextField.isRequired],
            [this.contractingAuthorityTitleLabel, "Hankija määratlus",
                !isAuthoringTemplate || !!locks.isContractingAuthorityTypeLocked],
            [this.submissionDeadlineTitleLabel, "Pakkumuste esitamise tähtaeg",
                this.submissionDeadlineDatePicker.isRequired],
            [this.publishEstimatedCostTitleLabel, "Avalda eeldatav kogumaksumus pakkujatele",
                !isAuthoringTemplate || !!locks.isPublishEstimatedCostLocked]
        ] as const) {
            label.innerHTML = title + IF(isRequired)(() =>
                ` <span style="color: ${RHColor.danger.stringValue}">*</span>`
            ).ELSE(() => "")
        }
        if (!this.procurementTitleTextField.isRequired) {
            this._updateInlineValidationMessage(this.procurementTitleValidationMessageLabel)
            this.procurementTitleTextField.resetValidationVisuals()
        }
        for (const [category, button] of Object.entries(this._templateLockButtons)) {
            const configuration = this._templateLockConfigurationForCategory(category as RHProcurementTemplateLockCategory)
            if ((isAuthoringTemplate || configuration.locked) &&
                !this._isTemplateFieldHidden(category as RHProcurementTemplateLockCategory)) {
                button.configure(configuration)
                button.enabled = isAuthoringTemplate
                button.addedAsSubviewToView(this.view)
            }
            else {
                button.removeFromSuperview()
            }
        }
        if (!isAuthoringTemplate && this._sourceTemplateLocks?.isTeamLocked) {
            this.procurementTeamView.configureTemplateLock(this._templateLockConfigurationForCategory("team"))
            this.procurementTeamView.templateLockButton.enabled = isAuthoringTemplate
        }
        else {
            this.procurementTeamView.configureTemplateLock(undefined)
        }
        this._updatePartTerminology()
        if (isAuthoringTemplate) {
            this.partsEditorView.configureTemplateAuthoringLocks({
                description: this._templateLockConfigurationForCategory("description"),
                estimatedCost: this._templateLockConfigurationForCategory("estimatedCost"),
                contractDuration: this._templateLockConfigurationForCategory("contractDuration"),
                cpv: this._templateLockConfigurationForCategory("cpv"),
                invitedCompanies: this._templateLockConfigurationForCategory("invitedCompanies")
            })
            this.requiredDocumentsEditorView.configureTemplateAuthoringLock(
                this._templateLockConfigurationForCategory("requiredDocuments")
            )
        }
        else {
            this.partsEditorView.configureTemplateAuthoringLocks(undefined)
            this.requiredDocumentsEditorView.configureTemplateAuthoringLock(undefined)
        }
    }


    _applyTemplateFieldVisibility() {
        const fields: Array<{ category: RHProcurementTemplateLockCategory; views: UIView[] }> = [
            { category: "title", views: [this.procurementTitleLabel, this.procurementTitleTextField] },
            { category: "estimatedCost", views: [this.costSummaryView] },
            { category: "publicationVisibility", views: [this.publicationModeTitleLabel,
                this.publicProcurementModeButton, this.directedProcurementModeButton] },
            { category: "publishEstimatedCost", views: [this.publishEstimatedCostTitleLabel,
                this.publishEstimatedCostYesButton, this.publishEstimatedCostNoButton] },
            { category: "maxOffersPerPart", views: [this.allowMultipleOffersPerPartCheckbox] },
            { category: "deadlineDate", views: [this.submissionDeadlineTitleLabel, this.submissionDeadlineDatePicker] },
            { category: "deadlineExtension", views: [this.autoExtendProcurementCheckbox,
                this.lessThanOffersTextField, this.autoExtendProcurementMiddleLabel,
                this.extendDeadlineDaysTextField, this.autoExtendProcurementEndLabel] },
            { category: "requiredDocuments", views: [this.requiredDocumentsEditorView] },
            { category: "files", views: [this.fileUploadSectionTitleLabel, this.fileUploadView] }
        ]
        for (const field of fields) {
            for (const view of field.views) {
                if (this._isTemplateFieldHidden(field.category)) {
                    view.removeFromSuperview()
                }
                else {
                    view.addedAsSubviewToView(this.view)
                }
            }
        }
        for (const [category, button] of Object.entries(this._templateLockButtons)) {
            if (this._isTemplateFieldHidden(category as RHProcurementTemplateLockCategory)) {
                button.removeFromSuperview()
            }
        }
        if (this._mode === "template" && !this._isPublishedAmendmentMode) {
            this.templateNameLabel.addedAsSubviewToView(this.view)
            this.templateNameTextField.addedAsSubviewToView(this.view)
            this.procurementTeamView.removeFromSuperview()
            this.templateManagersView.addedAsSubviewToView(this.view)
            this.showTeamInDerivedProcurementsCheckbox.addedAsSubviewToView(this.view)
        }
        else {
            this.templateNameLabel.removeFromSuperview()
            this.templateNameTextField.removeFromSuperview()
            this.templateManagersView.removeFromSuperview()
            this.showTeamInDerivedProcurementsCheckbox.removeFromSuperview()
            if (this._isTemplateFieldHidden("team") ||
                (!this._isPublishedAmendmentMode && !this._shouldShowTeamFromSourceTemplate)) {
                this.procurementTeamView.removeFromSuperview()
            }
            else {
                this.procurementTeamView.addedAsSubviewToView(this.view)
            }
        }
        const isPartsEditorHidden = (["parts", "description", "estimatedCost", "cpv", "invitedCompanies",
            "contractDuration"] as const).every(category => this._isTemplateFieldHidden(category))
        if (isPartsEditorHidden) {
            this.partsEditorView.removeFromSuperview()
        }
        else {
            this.partsEditorView.addedAsSubviewToView(this.view)
        }
        if (this._isTemplateFieldHidden("title")) {
            this.procurementTitleValidationMessageLabel.removeFromSuperview()
        }
        if (this._isTemplateFieldHidden("deadlineDate")) {
            this.submissionDeadlineValidationMessageLabel.removeFromSuperview()
        }
        if (this._isTemplateFieldHidden("publishEstimatedCost")) {
            this.publishEstimatedCostValidationMessageLabel.removeFromSuperview()
        }
        this._updatePartTerminology()
        this.view.setNeedsLayoutUpToRootView()
    }


    _applyEditingRestrictionsToForm() {
        const isReconciliationRoundAmendment = this._isPublishedAmendmentMode &&
            !!this.class.procurement?.reconciliationParentProcurement
        let locks = this._sourceTemplateLocks
        if (isReconciliationRoundAmendment) {
            locks = {
                isTitleLocked: YES,
                isDescriptionLocked: YES,
                isEstimatedCostLocked: YES,
                isContractDurationLocked: YES,
                isCpvLocked: YES,
                isPartsLocked: YES,
                isRequiredDocumentsLocked: YES,
                isInvitedCompaniesLocked: YES,
                isPublicationVisibilityLocked: YES,
                isMaxOffersPerPartLocked: YES,
                isDeadlineExtensionLocked: YES,
                isPublishEstimatedCostLocked: YES,
                isTeamLocked: YES
            }
        }
        let readOnlyPartIds = IF(this._isPublishedAmendmentMode)(() =>
            this.class.procurement?.closedWithoutContractPartIds ?? []
        ).ELSE(() => [])
        if (isReconciliationRoundAmendment) {
            readOnlyPartIds = this.class.procurement!.parts.map((part: RHProcurementPart) => part._id!.toString())
        }
        this.partsEditorView.readOnlyPartIds = readOnlyPartIds
        this.requiredDocumentsEditorView.readOnlyPartIds = readOnlyPartIds
        this.procurementTitleTextField.configureWithObject({
            enabled: !locks?.isTitleLocked && !this._roundAmendmentIdentity,
            textElementView: { viewHTMLElement: { readOnly: !!locks?.isTitleLocked || !!this._roundAmendmentIdentity } }
        })
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const usesServerImportResolver = this._mode === "template" || !!this._sourceTemplateId ||
            !!(this.class.procurement as RHProcurement | undefined)?.sourceTemplate
        this.partsEditorView.configureTemplateProtection({
            description: !!locks?.isDescriptionLocked,
            estimatedCost: !!locks?.isEstimatedCostLocked,
            isDescriptionHidden: this._isTemplateFieldHidden("description"),
            isEstimatedCostHidden: this._isTemplateFieldHidden("estimatedCost"),
            cpv: !!locks?.isCpvLocked,
            isCpvHidden: this._isTemplateFieldHidden("cpv"),
            isPartsHidden: this._isTemplateFieldHidden("parts"),
            isContractDurationHidden: this._isTemplateFieldHidden("contractDuration"),
            isInvitedCompaniesHidden: this._isTemplateFieldHidden("invitedCompanies"),
            parts: !!locks?.isPartsLocked,
            contractDuration: !!locks?.isContractDurationLocked,
            requiredDocuments: !!locks?.isRequiredDocumentsLocked,
            invitedCompanies: !!locks?.isInvitedCompaniesLocked,
            canOverrideLockedSupplierSourceCPVs:
                !!this._templateCapabilities?.canOverrideLockedSupplierSourceCPVs,
            persistsCompanyCustomization: !usesServerImportResolver,
            invitationImportResolver: usesServerImportResolver && companyId
                ? async rows => {
                    const result = await RHCore.sharedInstance.procurementStore
                        .resolveTemplateInvitationImportRows({ companyId, rows })
                    if (!result.success) {
                        throw new Error(result.errorCode)
                    }
                    return result.value
                }
                : undefined
        })
        this.requiredDocumentsEditorView.readOnly = !!locks?.isRequiredDocumentsLocked
        this._updateContractingAuthorityChoicesWithSelection(this._selectedContractingAuthorityType)
        this.publicProcurementModeButton.enabled = !locks?.isPublicationVisibilityLocked
        this.directedProcurementModeButton.enabled = !locks?.isPublicationVisibilityLocked
        this.submissionDeadlineDatePicker.enabled = !locks?.isDeadlineDateLocked
        this.autoExtendProcurementCheckbox.enabled = !locks?.isDeadlineExtensionLocked
        this.allowMultipleOffersPerPartCheckbox.enabled = !locks?.isMaxOffersPerPartLocked &&
            !this._hasMultipleSubmittedOffersPerPart
        if (this._hasMultipleSubmittedOffersPerPart) {
            this.allowMultipleOffersPerPartCheckbox.selected = YES
        }
        this.publishEstimatedCostYesButton.enabled = !locks?.isPublishEstimatedCostLocked
        this.publishEstimatedCostNoButton.enabled = !locks?.isPublishEstimatedCostLocked
        for (const [field, isLocked, isSelected] of [
            [this.lessThanOffersTextField, !!locks?.isDeadlineExtensionLocked, this.autoExtendProcurementCheckbox.selected],
            [this.extendDeadlineDaysTextField, !!locks?.isDeadlineExtensionLocked, this.autoExtendProcurementCheckbox.selected]
        ] as const) {
            field.configureWithObject({
                enabled: !isLocked && isSelected,
                textElementView: { viewHTMLElement: { readOnly: isLocked } }
            })
        }
        this.fileUploadView.enabled = !locks?.isFilesLocked
        this.fileUploadView.shouldDeleteFilesWhenRemovedFromList = !this._isPublishedAmendmentMode && !usesServerImportResolver &&
            !this.class.procurement?.sourceTemplate
        this.fileUploadView.fileItemViews.forEach(fileItemView => {
            fileItemView.shouldDeleteFileWhenRemovedFromList =
                this.fileUploadView.shouldDeleteFilesWhenRemovedFromList
            fileItemView.deleteButton.enabled = this.fileUploadView.enabled &&
                !(isReconciliationRoundAmendment && this.class.procurement?.files?.some(
                    (file: RHProcurement["files"][number]) => referenceId(file) === fileItemView.fileId
                ))
        })
        this.procurementTeamView.addsCurrentUserWhenMissing = !locks?.isTeamLocked
        this.procurementTeamView.configureRoleMatrixPresentation({
            visibleRoles: ["responsible", "authorized", "observer", "evaluator"],
            editableRoles: IF(!!locks?.isTeamLocked)(() => []).ELSE(() =>
                ["responsible", "authorized", "observer", "evaluator"]
            )
        })
        this._applyTemplateFieldVisibility()
    }


    _scheduleCPVSummationReport() {
        this._cancelCPVSummationReport()
        if (!this._currentCPVSummationReportRequest()) {
            this._updateCPVSummationReport(undefined)
            return
        }
        this._cpvSummationReportRefreshTimer = setTimeout(() => {
            this._cpvSummationReportRefreshTimer = undefined
            void this._refreshCPVSummationReport()
        }, 450)
    }


    async _refreshCPVSummationReport() {
        const requestSerial = ++this._cpvSummationReportRequestSerial
        const request = this._currentCPVSummationReportRequest()
        if (!request) {
            this._updateCPVSummationReport(undefined)
            return
        }
        try {
            const report = await RHCore.sharedInstance.procurementStore.retrieveCPVSummationReportForRequest(request)
            let isTemplateContractExportAllowed = NO
            if (requestSerial === this._cpvSummationReportRequestSerial && report?.templateSummary && this._templateIdForContractReport) {
                const capabilities = await RHCore.sharedInstance.procurementStore.retrieveProcurementCapabilities(
                    this._templateIdForContractReport
                )
                isTemplateContractExportAllowed = capabilities.canExportTemplateUsage
            }
            if (requestSerial !== this._cpvSummationReportRequestSerial) {
                return
            }
            this._isTemplateContractExportAllowed = isTemplateContractExportAllowed
            this._updateCPVSummationReport(report, request)
        }
        catch (error) {
            if (requestSerial !== this._cpvSummationReportRequestSerial) {
                return
            }
            this._updateCPVSummationReport(undefined)
            let errorMessage = "CPV summeerimise laadimine ebaõnnestus."
            if (error instanceof Error) {
                errorMessage = error.message
            }
            this.cpvSummationErrorLabel.text = errorMessage
            this.cpvSummationErrorLabel.addedAsSubviewToView(this.view)
            this.view.setNeedsLayoutUpToRootView()
        }
    }


    _cancelCPVSummationReport() {
        if (this._cpvSummationReportRefreshTimer) {
            clearTimeout(this._cpvSummationReportRefreshTimer)
            this._cpvSummationReportRefreshTimer = undefined
        }
        this._cpvSummationReportRequestSerial += 1
    }


    _loadRegistryProcurement(): Promise<void> {
        const registryReferenceNumber = this.registryReferenceTextField.text.trim()
        if (registryReferenceNumber === this._registryLookupReferenceNumber && this._registryLookupPromise) {
            return this._registryLookupPromise
        }
        this._resetRegistryLookup()
        if (!registryReferenceNumber) return Promise.resolve()
        this._registryLookupReferenceNumber = registryReferenceNumber
        if (!/^\d{6}$/.test(registryReferenceNumber)) {
            this._registryLookupMessage = "Viitenumber peab koosnema kuuest numbrist."
            this._updateRegistryViews()
            return Promise.resolve()
        }
        const requestSerial = ++this._registryLookupRequestSerial
        this.registryLoadingView.loading = YES
        this._updateRegistryViews()
        this._registryLookupPromise = (async () => {
            try {
                const procurement = await RHCore.sharedInstance.procurementStore.retrieveRegistryProcurementForReferenceNumber(
                    registryReferenceNumber
                )
                if (requestSerial !== this._registryLookupRequestSerial ||
                    registryReferenceNumber !== this.registryReferenceTextField.text.trim()) return
                this._registryProcurement = procurement ?? undefined
                this._registryLookupMessage = IF(procurement)(() => "").ELSE(() => "Selle viitenumbriga riigihanget ei leitud.")
                this._pendingRegistryLots = procurement?.lots ?? []
                this.registryLotDropdown.configureWithObject({
                    options: this._pendingRegistryLots.map(lot => ({ label: lot.partNumber + " — " + lot.title, value: lot.partNumber })),
                    selectedValue: this.registryPartTextField.text.trim() || undefined
                })
                if (procurement && !procurement.lots.length && this._mode === "template") {
                    this.registryPartTextField.text = ""
                }
            }
            catch (error) {
                if (requestSerial !== this._registryLookupRequestSerial) return
                this._registryLookupPromise = undefined
                this._registryLookupMessage = "Riigihanke andmeid ei õnnestunud laadida. Proovi uuesti."
            }
            if (requestSerial === this._registryLookupRequestSerial) {
                this.registryLoadingView.loading = NO
                this._updateRegistryViews()
            }
        })()
        return this._registryLookupPromise
    }


    _resetRegistryLookup() {
        this._registryLookupRequestSerial += 1
        this.registryLoadingView.loading = NO
        this._registryLookupReferenceNumber = undefined
        this._registryLookupPromise = undefined
        this._registryProcurement = undefined
        this._pendingRegistryLots = []
        this._registryLookupMessage = ""
        this._updateRegistryViews()
    }


    _updateRegistryViews() {
        const visibleViews: UIView[] = []
        if (!this.registryReferenceTextField.hidden) {
            if (this.registryLoadingView.loading) visibleViews.push(this.registryLoadingView)
            this.registryTitleBanner.alertType = IF(this._registryProcurement)(() => RHAlertBannerView.type.Success)
                .ELSE(() => RHAlertBannerView.type.Info)
            this.registryTitleBanner.text = RHTextSanitizer.escapedHTML(this._registryProcurement?.title ?? this._registryLookupMessage)
            if (this.registryTitleBanner.text) visibleViews.push(this.registryTitleBanner)
            if (this._pendingRegistryLots.length ||
                (this._mode !== "template" && this.registryPartTextField.text.trim())) {
                visibleViews.push(this.registryPartLabel)
                if (this._mode === "template") visibleViews.push(this.registryLotDropdown)
                else visibleViews.push(this.registryPartTextField)
                const lot = this._pendingRegistryLots.find(item => item.partNumber === this.registryPartTextField.text.trim())
                this.registryPartTitleBanner.text = RHTextSanitizer.escapedHTML(lot?.title ?? "")
                if (lot && this._mode !== "template") visibleViews.push(this.registryPartTitleBanner)
                else if (!lot && this._mode === "template") {
                    this.registryPartSelectionBanner.text = "Hange on jaotatud osadeks (" + this._pendingRegistryLots.length +
                        "). Vali allpool osa, mille kohta mall käib."
                    visibleViews.push(this.registryPartSelectionBanner)
                }
            }
        }
        for (const view of [this.registryLoadingView, this.registryTitleBanner, this.registryPartLabel, this.registryPartTextField,
            this.registryLotDropdown, this.registryPartSelectionBanner, this.registryPartTitleBanner]) {
            if (visibleViews.includes(view)) {
                if (view.superview !== this.view) view.addedAsSubviewToView(this.view)
            }
            else {
                view.removeFromSuperview()
            }
        }
        this.view.setNeedsLayoutUpToRootView()
    }


    _applyRegistryLotToForm(lot: RHRegistryProcurementLot) {
        this.registryPartTextField.text = lot.partNumber
        this._updateRegistryViews()
        this._markDirty()
        this._scheduleCPVSummationReport()
    }


    _currentCPVSummationReportRequest(): RHCPVSummationReportRequest | undefined {
        const buyerId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const contractingAuthorityType = this._selectedContractingAuthorityType
        if (!buyerId || !contractingAuthorityType || contractingAuthorityType === "outsideRHS") {
            return undefined
        }
        const procurementParts = this.partsEditorView.procurementParts
        const parts: RHCPVSummationReportRequest["parts"] = []
        for (const [partIndex, procurementPart] of procurementParts.entries()) {
            const cpvText = this.partsEditorView._partEditorViews[partIndex].cpvCodeForSummation
            if (!RHCPVCodeFormatter.normalizedCPV8FromText(cpvText)) {
                continue
            }
            const requestPart: RHCPVSummationReportRequest["parts"][number] = {
                partNumber: procurementPart.partNumber ?? partIndex + 1,
                cpv9: RHCPVCodeFormatter.displayCPV9FromText(cpvText) || cpvText
            }
            if (typeof procurementPart.estimatedCost === "number") {
                requestPart.amountExclVat = procurementPart.estimatedCost
            }
            if (procurementPart.contractDuration) {
                requestPart.contractDuration = procurementPart.contractDuration
            }
            parts.push(requestPart)
        }
        return {
            buyerId,
            contractingAuthorityType,
            procurementId: this.class.procurement?._id?.toString(),
            sourceTemplateId: this._sourceTemplateId,
            sourceTemplateRegistryReferenceNumber: this.registryReferenceTextField.text.trim(),
            sourceTemplateRegistryPartNumber: this.registryPartTextField.text.trim(),
            template: IF(this._mode === "template")(() => ({
                registryReferenceNumber: this.registryReferenceTextField.text.trim(),
                registryPartNumber: this.registryPartTextField.text.trim()
            })).ELSE(() => undefined),
            itemType: "HANGE",
            procurementPartCount: procurementParts.length,
            parts
        }
    }


    _summationInfoForEditorCPVCode(
        editor: ProcurementPartEditorView,
        cpvCode: string,
        shouldLoad = YES
    ): CPVSummationRowInfo | undefined {
        if (!this.partsEditorView._partEditorViews.includes(editor)) {
            return undefined
        }
        return this._summationInfoFromReportForCPVCode(this._latestCPVSummationReport, cpvCode)
    }


    _summationInfoFromReportForCPVCode(
        report: RHCPVSummationReport | undefined,
        cpvCode: string
    ): CPVSummationRowInfo | undefined {
        const cpv8 = RHCPVCodeFormatter.normalizedCPV8FromText(cpvCode)
        if (!cpv8 || !report || report.scope.contractingAuthorityType === "outsideRHS" || report.templateSummary) {
            return undefined
        }
        return {
            amount: report.totalsByCPV[cpv8] ?? 0,
            isOverLimit: report.levels.some(level => level.cpvAtLevel === cpv8 && level.status === "red")
        }
    }


    _updateCPVSummationReport(
        report: RHCPVSummationReport | undefined,
        request?: RHCPVSummationReportRequest
    ) {
        this._latestCPVSummationReport = report
        this.cpvSummationErrorLabel.removeFromSuperview()
        if (report?.templateSummary && report.scope.contractingAuthorityType !== "outsideRHS") {
            this.templateContractReportView.updateWithReport(report)
            this.templateContractReportView.addedAsSubviewToView(this.view)
            this._updateTemplateContractExportAvailability()
        }
        else {
            this.templateContractReportView.removeFromSuperview()
        }
        this.partsEditorView.updateCPVSummationReport(report, request)
        for (const editor of this.partsEditorView._partEditorViews) {
            editor.cpvTreeDialog.refreshDisplayedRows()
        }
        this.view.setNeedsLayoutUpToRootView()
    }


    _updatePublishEstimatedCostValidationMessageVisibility(isVisible: boolean) {
        const wasVisible = this.publishEstimatedCostValidationMessageLabel.superview === this.view

        if (isVisible && !this._isTemplateFieldHidden("publishEstimatedCost")) {
            this.publishEstimatedCostValidationMessageLabel.text = this.publishEstimatedCostValidationMessage
            this.publishEstimatedCostValidationMessageLabel.addedAsSubviewToView(this.view)
        }
        else if (wasVisible) {
            this.publishEstimatedCostValidationMessageLabel.removeFromSuperview()
        }

        if (wasVisible !== isVisible) {
            this.view.setNeedsLayoutUpToRootView()
        }
    }


    _updateInlineValidationMessage(label: UITextView, validationMessage?: string) {
        const wasVisible = label.superview === this.view
        if (validationMessage && !(label === this.submissionDeadlineValidationMessageLabel &&
            this._isTemplateFieldHidden("deadlineDate"))) {
            label.text = validationMessage
            label.addedAsSubviewToView(this.view)
        }
        else if (wasVisible) {
            label.removeFromSuperview()
        }
        if (wasVisible !== !!validationMessage) {
            this.view.setNeedsLayoutUpToRootView()
        }
    }


    _validateProcurementTitle(): boolean {
        const isValid = this.procurementTitleTextField.validate()
        let validationMessage: string | undefined
        if (!isValid) {
            validationMessage = "Hanke nimetus on kohustuslik."
        }
        this._updateInlineValidationMessage(
            this.procurementTitleValidationMessageLabel,
            validationMessage
        )
        return isValid
    }


    _validateSubmissionDeadline(currentDate: Date = new Date()): string | undefined {
        this.submissionDeadlineDatePicker.minDate = currentDate
        let validationMessage: string | undefined
        if (!this.submissionDeadlineDatePicker.validate()) {
            validationMessage = "Pakkumuste esitamise tähtaeg on kohustuslik."
            const selectedDeadline = this.submissionDeadlineDatePicker.selectedDate
            if (selectedDeadline && selectedDeadline.getTime() < currentDate.getTime()) {
                validationMessage = "Pakkumuste esitamise tähtaeg peab olema tulevikus."
            }
        }
        this._updateInlineValidationMessage(
            this.submissionDeadlineValidationMessageLabel,
            validationMessage
        )
        return validationMessage
    }


    _clearExpiredDraftDeadline() {
        if (this._mode === "template" || this._isPublishedAmendmentMode ||
            this._sourceTemplateLocks?.isDeadlineDateLocked) {
            return
        }
        const deadlineDate = this.submissionDeadlineDatePicker.selectedDate
        if (deadlineDate && deadlineDate.getTime() <= Date.now()) {
            this.submissionDeadlineDatePicker.clear()
            this._updateInlineValidationMessage(
                this.submissionDeadlineValidationMessageLabel,
                "Pakkumuste esitamise tähtaeg on kohustuslik."
            )
        }
    }


    // Shared by _validateForSubmission (the full validation-issues dialog) and
    // saveDraft's own earlier short-circuit check, so both actually focus the
    // right control on dismiss instead of one of them silently doing nothing.
    _contractingAuthorityValidationIssue(): RHValidationIssue | undefined {
        if (this._mode === "template" && !this._currentTemplateLocks().isContractingAuthorityTypeLocked) {
            return undefined
        }
        if (this._selectedContractingAuthorityType) {
            return undefined
        }
        return {
            focusTarget: this.contractingAuthorityButtons.find(
                option => option.button.superview === this.contractingAuthorityChoicesView
            )?.button ?? this.contractingAuthorityTitleLabel,
            message: "Vali hankija määratlus."
        }
    }


    _validateForSubmission(): boolean {
        const validationIssues: RHValidationIssue[] = []
        const templateLocks = IF(this._mode === "template")(() => this._currentTemplateLocks()).ELSE(() => undefined)
        if (templateLocks) {
            this.templateNameTextField.text = this.templateNameTextField.text.trim()
            if (!this.templateNameTextField.validate()) {
                validationIssues.push({ focusTarget: this.templateNameTextField, message: "Malli nimetus on puudu." })
            }
            if (this.registryReferenceTextField.text.trim()) {
                if (!this._registryProcurement ||
                    this._registryLookupReferenceNumber !== this.registryReferenceTextField.text.trim()) {
                    validationIssues.push({ focusTarget: this.registryReferenceTextField,
                        message: this._registryLookupMessage || "Kontrolli riigihangete registri viitenumbrit." })
                }
                else if (this._pendingRegistryLots.length && !this._pendingRegistryLots.some(
                    lot => lot.partNumber === this.registryPartTextField.text.trim()
                )) {
                    validationIssues.push({ focusTarget: this.registryLotDropdown,
                        message: "Vali riigihangete registri osanumber." })
                }
            }
        }

        const contractingAuthorityIssue = this._contractingAuthorityValidationIssue()
        if (contractingAuthorityIssue) {
            validationIssues.push(contractingAuthorityIssue)
        }

        this.procurementTitleTextField.text = this.procurementTitleTextField.text.trim()
        if (!this._validateProcurementTitle()) {
            validationIssues.push({
                focusTarget: this.procurementTitleTextField,
                message: "Hanke nimetus on puudu."
            })
        }

        if (
            (!templateLocks || templateLocks.isInvitedCompaniesLocked) &&
            !this._isPublishedAmendmentMode &&
            this._selectedPublicationVisibility === "directed" &&
            this.partsEditorView.invitedCompanies.length === 0
        ) {
            validationIssues.push({
                focusTarget: this.partsEditorView._partEditorViews.firstElement?.openInvitedCompaniesButton ??
                    this.partsEditorView,
                message: "Mitteavaliku hanke eelvaatesse liikumiseks vali vähemalt üks osalema kutsutav ettevõte."
            })
        }

        validationIssues.push(...this.partsEditorView.validateFields())

        const isPublishEstimatedCostSelectionValid =
            (!!templateLocks && !templateLocks.isPublishEstimatedCostLocked) || !!this.publishEstimatedCostButtonGroup.value
        this._updatePublishEstimatedCostValidationMessageVisibility(!isPublishEstimatedCostSelectionValid)
        if (!isPublishEstimatedCostSelectionValid) {
            validationIssues.push({
                focusTarget: this.publishEstimatedCostYesButton,
                message: this.publishEstimatedCostValidationMessage
            })
        }

        validationIssues.push(...this.requiredDocumentsEditorView.validateFields())

        if (!templateLocks || templateLocks.isDeadlineDateLocked) {
            const currentDate = new Date()
            const message = this._validateSubmissionDeadline(currentDate)
            if (message) {
                validationIssues.push({
                    focusTarget: this.submissionDeadlineDatePicker,
                    message
                })
            }
        }
        else {
            this._updateInlineValidationMessage(this.submissionDeadlineValidationMessageLabel)
        }

        if (this.autoExtendProcurementCheckbox.selected && (!templateLocks || templateLocks.isDeadlineExtensionLocked)) {
            if (!this.lessThanOffersTextField.validate()) {
                validationIssues.push({
                    focusTarget: this.lessThanOffersTextField,
                    message: "Automaatse pikendamise pakkumuste arv peab olema vähemalt 1."
                })
            }

            if (!this.extendDeadlineDaysTextField.validate()) {
                validationIssues.push({
                    focusTarget: this.extendDeadlineDaysTextField,
                    message: "Automaatse pikendamise päevade arv peab olema vähemalt 1."
                })
            }
        }

        const teamValidationMessage = this.procurementTeamView.validationMessage()
        if (this._mode !== "template" && teamValidationMessage != undefined) {
            validationIssues.push({
                focusTarget: this.procurementTeamView,
                message: teamValidationMessage
            })
        }

        if (
            this._isPublishedAmendmentMode &&
            !this.changeExplanationTextArea.validate()
        ) {
            validationIssues.push({
                focusTarget: this.changeExplanationTextArea,
                message: "Muudatuse selgitus on puudu."
            })
        }

        if (validationIssues.length > 0) {
            const firstValidationIssue = validationIssues.firstElement
            CBDialogViewShower.showContentAlert(
                new RHValidationIssuesDialogView(validationIssues),
                () => firstValidationIssue?.focusTarget.focus()
            )
            return NO
        }

        return YES

    }


    _amendmentInput(): RHAmendPublishedProcurementInput {
        let procurement: RHAmendPublishedProcurementInput["procurement"]
        const sourceProcurement: WithNullsForOptional<RHProcurementFormSource> | undefined = this.class.procurement
        if (this._isPublishedAmendmentMode && sourceProcurement?.reconciliationParentProcurement) {
            const { procuringCompany, primaryProcuringUser, registeredTenderers, ...protectedProcurementFields } = sourceProcurement
            procurement = {
                ...protectedProcurementFields,
                deadlineDate: this.submissionDeadlineDatePicker.selectedDate || null,
                files: this.fileUploadView.uploadedFiles.map(file => file.id)
            }
        }
        else {
            procurement = this._currentProcurementPayloadWithStatus("published")
        }
        const changeExplanation = this.changeExplanationTextArea.text.trim()
        const input: RHAmendPublishedProcurementInput = {
            procurement,
            changeExplanation,
            expectedPublishedVersion: this.class.procurement?.publishedVersion ?? 0,
            expectedUpdatedAt: this.class.procurement?.updatedAt
        }

        return input
    }


    _confirmAmendmentPreview(preview: RHProcurementAmendmentPreview): Promise<boolean> {
        const changeLines = preview.changes.map(change => {
            if (change.kind === "value") {
                return `${change.label}: ${change.displayBefore} → ${change.displayAfter}`
            }
            return change.text
        })
        let notificationExplanation =
            "Muudatus ja selgitus avaldatakse teabevahetuses ning pakkujatele saadetakse teavitus."
        if (preview.hasOnlyInvitedCompanyChanges) {
            notificationExplanation = "Muudatus salvestatakse ja uutele kutsutavatele saadetakse kutse."
        }
        const supportingText = [
            notificationExplanation,
            "",
            "<strong>Muudatused:</strong>",
            ...changeLines.map(line => RHTextSanitizer.escapedHTML(line)),
            "",
            `<strong>Selgitus:</strong> ${RHTextSanitizer.escapedHTML(preview.changeExplanation)}`
        ].join("<br>")
        return new Promise(resolve => {
            const shower = CBDialogViewShower.showQuestionDialogWithSupportingText(
                "Kas oled kindel, et soovid hanget muuta?",
                supportingText,
                () => resolve(NO)
            )
            shower.dialogView.view.yesButton.titleLabel.text = "Salvesta muudatus"
            shower.dialogView.view.noButton.titleLabel.text = "Tühista"
            shower.dialogView.view.noButtonDismissesDialog = YES
            shower.dialogView.view.setNeedsLayoutUpToRootView()
            shower.yesButtonWasPressed = () => {
                // dismiss() invokes the NO callback synchronously, so confirm first.
                resolve(YES)
                shower.dialogView.dismiss()
            }
        })
    }


    async _savePublishedAmendment() {
        if (this.partsEditorView.procurementParts.length === 0) {
            CBDialogViewShower.alert("Hange peab sisaldama vähemalt ühte osa")
            return
        }

        await this.partsEditorView.prepareForValidation()

        if (!this._validateForSubmission()) {
            return
        }

        const input = this._amendmentInput()
        let preview: RHProcurementAmendmentPreview
        try {
            CBDialogViewShower.showActionIndicatorDialog("Muudatuse eelvaadet koostatakse...")
            preview = await RHCore.sharedInstance.procurementStore.previewPublishedProcurementAmendment(
                input
            )
        }
        catch (error) {
            CBDialogViewShower.alert(
                (messageOfError(error) || "Hanke muudatuse eelvaate koostamine ebaõnnestus.")
            )
            return
        }
        finally {
            CBDialogViewShower.hideActionIndicatorDialog()
        }

        if (!preview.hasChanges) {
            CBDialogViewShower.alert("Hankes ei ole salvestatavaid muudatusi.")
            return
        }

        const shouldSave = await this._confirmAmendmentPreview(preview)
        if (shouldSave !== YES) {
            return
        }

        try {
            CBDialogViewShower.showActionIndicatorDialog("Muudatust salvestatakse...")
            const result = await RHCore.sharedInstance.procurementStore.amendPublishedProcurement(
                input
            )
            this.class.procurement = result
            this._isDirty = NO
            let successMessage =
                "Hanke muudatus salvestati ja lisati teabevahetusse. Pakkujate teavitused on saadetud või saatmisjärjekorras."
            if (preview.hasOnlyInvitedCompanyChanges) {
                successMessage = "Hanke muudatus salvestati. Uutele kutsutavatele saadetakse kutse."
            }
            CBDialogViewShower.alert(
                successMessage,
                () => this._routeBackToProcurementDetails()
            )
        }
        catch (error) {
            CBDialogViewShower.alert(
                (messageOfError(error) || "Hanke muudatuse salvestamine ebaõnnestus.")
            )
        }
        finally {
            CBDialogViewShower.hideActionIndicatorDialog()
        }
    }


    _currentProcurementPayloadWithStatus(
        status: "draft" | "published"
    ): Parameters<typeof SocketClient.AddOrModifyNewProcurement>[0] {
        // Collect required documents per part number
        const requiredDocsByPartNumber = this.requiredDocumentsEditorView.requiredDocumentsByPartNumber()
        const isInitialTemplateInstantiation =
            !this.class.procurement?._id && !!this._sourceTemplateId

        // Collect parts data
        const parts: WithNullsForOptional<Partial<RHProcurementPart>>[] = this.partsEditorView.procurementParts
            .map((part, index) => {
                const payloadPart = {
                    ...part,
                    requiredDocuments: requiredDocsByPartNumber[index + 1] ?? []
                }
                if (this.partsEditorView.readOnlyPartIds.includes(part._id?.toString() ?? "")) {
                    payloadPart.requiredDocuments = part.requiredDocuments ?? []
                }
                if (this.class.procurement?.reconciliationParentProcurement) {
                    const sourcePart = this.class.procurement.parts.find((candidate: RHProcurementPart) =>
                        candidate._id?.toString() === part._id?.toString()
                    )
                    if (sourcePart) {
                        // Editor positions are local; round parts retain their source numbering.
                        payloadPart.partNumber = sourcePart.partNumber
                    }
                }
                if (!isInitialTemplateInstantiation) {
                    delete payloadPart.sourceTemplatePartId
                }
                return payloadPart
            })

        let maxOffersPerPart: number | null = null
        if (this.allowMultipleOffersPerPartCheckbox.selected) {
            maxOffersPerPart = 0
        }

        let minimumAcceptableNumberOfOffers: number | null = null
        let numberOfDaysToExtendDeadlineByIfTooFewOffers: number | null = null
        if (this.autoExtendProcurementCheckbox.selected) {
            minimumAcceptableNumberOfOffers = Number(this.lessThanOffersTextField.text || 0)
            numberOfDaysToExtendDeadlineByIfTooFewOffers = Number(this.extendDeadlineDaysTextField.text || 0)
        }

        let publishEstimatedCostOfProcurement: boolean | null = null
        if (this.publishEstimatedCostButtonGroup.value === "yes") {
            publishEstimatedCostOfProcurement = YES
        }
        else if (this.publishEstimatedCostButtonGroup.value === "no") {
            publishEstimatedCostOfProcurement = NO
        }

        let procurementTitle = this.procurementTitleTextField.text || null
        if (this._isPublishedAmendmentMode && this._roundAmendmentIdentity) {
            procurementTitle = this.class.procurement?.title ?? null
        }
        return {
            _id: this.class.procurement?._id,
            companyId: RHCore.sharedInstance.companyAffiliationStore.activeCompanyId,
            sourceTemplateId: this._sourceTemplateId,
            expectedTemplateUpdatedAt: this._sourceTemplateId
                ? this._expectedTemplateUpdatedAt
                : undefined,
            templateInstantiationKey: this._sourceTemplateId
                ? this._templateInstantiationKey
                : undefined,

            ...IF(this._mode === "template")(() => ({
                templateName: this.templateNameTextField.text.trim(),
                templateManagerUsers: this.templateManagersView.authorizedUserIds,
                shouldShowTeamInDerivedProcurements: this.showTeamInDerivedProcurementsCheckbox.selected
            })).ELSE(() => ({})),
            procurementType: "newProcurement",
            isCompetition: this._selectedContractingAuthorityType === "outsideRHS",
            contractingAuthorityType: this._selectedContractingAuthorityType,
            registryReferenceNumber: this.registryReferenceTextField.text.trim(),
            registryPartNumber: this.registryPartTextField.text.trim(),
            spendCategory: RHCPVCodeFormatter.spendCategoryForCPVCodes(
                parts.map(procurementPart => procurementPart.CPV)
            ),
            status,
            publicationVisibility: this._selectedPublicationVisibility,
            summationLastCheck: this._latestCPVSummationReport ?? null,

            responsibleProcuringUser: this.procurementTeamView.responsibleUserId,
            authorizedProcuringUsers: this.procurementTeamView.authorizedUserIds,
            observerProcuringUsers: this.procurementTeamView.observerUserIds,
            evaluatorProcuringUsers: this.procurementTeamView.evaluatorUserIds,
            secondaryProcuringUsers: this.procurementTeamView.authorizedUserIds,

            title: procurementTitle,

            deadlineDate: this.submissionDeadlineDatePicker.selectedDate || null,

            minimumAcceptableNumberOfOffers,

            numberOfDaysToExtendDeadlineByIfTooFewOffers,

            maxOffersPerPart: maxOffersPerPart,

            publishEstimatedCostOfProcurement,

            parts: parts,
            selectedLanguages: ["Eesti"],

            files: this.fileUploadView.uploadedFiles.map(file => file.id),

            // Send the selected-only snapshot projected from the current live
            // part editors. The server revalidates and replaces it atomically.
            invitedCompanies: this.partsEditorView.invitedCompanies,

            allowUnregisteredTenderers: YES
        } as Parameters<typeof SocketClient.AddOrModifyNewProcurement>[0]
    }


    async saveDraft(options?: { allowsIncompleteForm?: boolean }) {

        // The framework does not await viewWillAppear before calling
        // viewDidAppear. If the user can act while a saved draft is still being
        // restored, wait for that same hydration before reading or validating
        // the form so a transient empty editor cannot win the race.
        if (this._formHydrationPromise) {
            await this._formHydrationPromise
        }

        // Validate that we have at least one part
        if (this.partsEditorView.procurementParts.length === 0) {
            CBDialogViewShower.alert("Hange peab sisaldama vähemalt ühte osa")
            return null
        }

        await this.partsEditorView.prepareForValidation()

        this._clearExpiredDraftDeadline()

        const contractingAuthorityIssue = this._contractingAuthorityValidationIssue()
        if (contractingAuthorityIssue) {
            CBDialogViewShower.showContentAlert(
                new RHValidationIssuesDialogView([contractingAuthorityIssue]),
                () => contractingAuthorityIssue.focusTarget.focus()
            )
            return null
        }
        if (this._mode === "template") await this._loadRegistryProcurement()
        if ((this._mode === "template" || !options?.allowsIncompleteForm) && !this._validateForSubmission()) {
            return null
        }

        if (this._mode !== "template" && options?.allowsIncompleteForm) {
            const validationIssues = this.requiredDocumentsEditorView.validateFields({ canOmitDocumentNames: YES })
            if (validationIssues.length > 0) {
                CBDialogViewShower.showContentAlert(
                    new RHValidationIssuesDialogView(validationIssues),
                    () => validationIssues.firstElement?.focusTarget.focus()
                )
                return null
            }
        }

        const procurement = this._currentProcurementPayloadWithStatus("draft")
        const formSessionSnapshotKey = this._formSessionSnapshotKey()

        if (this._mode === "template") {
            const {
                companyId,
                sourceTemplateId: _sourceTemplateId,
                expectedTemplateUpdatedAt: _sourceExpectedVersion,
                templateInstantiationKey: _templateInstantiationKey,
                ...formData
            } = procurement
            if (!companyId) {
                CBDialogViewShower.alert("Ettevõtte konteksti ei leitud.")
                return null
            }
            const templateId = this.class.procurement?._id?.toString()
            let result: Awaited<ReturnType<
                typeof RHCore.sharedInstance.procurementStore.saveProcurementTemplate
            >>
            try {
                result = await RHCore.sharedInstance.procurementStore.saveProcurementTemplate({
                    companyId,
                    templateId,
                    expectedTemplateUpdatedAt: templateId
                        ? this._expectedTemplateUpdatedAt
                        : undefined,
                    formData,
                    templateLocks: this._currentTemplateLocks()
                })
            }
            catch (error) {
                console.error("NewProcurementViewController: template save failed", error)
                CBDialogViewShower.alert("Hankemalli salvestamine ebaõnnestus.")
                return null
            }
            if (!result.success) {
                let message = "Hankemalli salvestamine ebaõnnestus."
                if (result.errorCode === "TEMPLATE_VERSION_CONFLICT") {
                    message = "Hankemall on vahepeal muutunud. Värskenda vorm enne jätkamist."
                }
                else if (result.errorCode === "TEMPLATE_ARCHIVED") {
                    message = "Arhiveeritud hankemalli ei saa muuta."
                }
                else if (result.errorCode === "TEMPLATE_REGISTRY_REFERENCE_EXISTS") {
                    message = "Selle viitenumbriga hankemall on juba olemas."
                }
                if (
                    result.errorCode === "TEMPLATE_VERSION_CONFLICT" ||
                    result.errorCode === "TEMPLATE_ARCHIVED"
                ) {
                    this.nextButton.enabled = NO
                }
                CBDialogViewShower.alert(message)
                return null
            }
            const savedTemplate = result.value.template
            this.class.procurement = savedTemplate
            this._expectedTemplateUpdatedAt = result.value.expectedTemplateUpdatedAt
            this._templateCapabilities = result.value.capabilities
            this._basisMode = "edit-template"
            this._isDirty = NO
            this._scheduleCPVSummationReport()
            if (formSessionSnapshotKey) {
                RHFormSessionSnapshotStore.removeSnapshotForKey(formSessionSnapshotKey)
            }
            return savedTemplate
        }

        let savedProcurement: RHProcurement | undefined

        try {
            const saveResult = await RHCore.sharedInstance.procurementStore.saveOrModifyProcurementResult(
                // @ts-ignore
                procurement
            )
            if (!saveResult.accepted || !saveResult.procurement) {
                if (saveResult.lockCategory || this.class.procurement?.sourceTemplate || this._sourceTemplateId) {
                    await this._handleTemplateWriteRejection(saveResult.lockCategory)
                }
                else {
                    CBDialogViewShower.alert("Hanke salvestamine ebaõnnestus")
                }
                return null
            }
            savedProcurement = saveResult.procurement
        }
        catch (error) {
            console.error("NewProcurementViewController: procurement save failed", error)
            CBDialogViewShower.alert(
                (messageOfError(error) || "Hanke salvestamine ebaõnnestus")
            )
            return null
        }

        this.class.procurement = savedProcurement
        if (this._sourceTemplateId) {
            await this._applyProcurementToForm(savedProcurement, this.fileUploadView.uploadedFiles)
            this._sourceTemplateId = undefined
            this._sourceTemplateTitle = undefined
            this._sourceTemplateLocks = undefined
            this._expectedTemplateUpdatedAt = undefined
            this._templateInstantiationKey = undefined
            this._templateCapabilities = undefined
        }
        this._sourceTemplateLocks = savedProcurement.sourceTemplateLocks
        this._applyTemplateAuthoringLockControls()
        this._applyEditingRestrictionsToForm()
        this._basisMode = "edit-draft"
        this._isDirty = NO
        if (formSessionSnapshotKey) {
            RHFormSessionSnapshotStore.removeSnapshotForKey(formSessionSnapshotKey)
        }

        if (savedProcurement._id && !this.hostRouteComponentName) {
            UIRoute.currentRoute.routeBySettingParameterInComponent(
                NewProcurementViewController.routeComponentName,
                NewProcurementViewController.ParameterIdentifierName.id,
                savedProcurement._id + ""
            ).applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
        }

        return savedProcurement

    }


    async _handleTemplateWriteRejection(
        lockCategory?: RHProcurementTemplateLockCategory
    ) {
        const message = NewProcurementPreviewViewController._templateValidationMessage(lockCategory)

        const procurementId = this.class.procurement?._id?.toString()
        if (procurementId) {
            try {
                const canonicalDraft = await RHCore.sharedInstance.procurementStore
                    .refreshProcurement(procurementId)
                await this._applyProcurementToForm(canonicalDraft)
                this._sourceTemplateLocks = canonicalDraft.sourceTemplateLocks
                this._applyEditingRestrictionsToForm()
            }
            catch (error) {
                console.error("NewProcurementViewController: canonical draft reload failed", error)
            }
        }
        CBDialogViewShower.alert(message, () => this._focusTemplateLockCategory(lockCategory))
    }


    _focusTemplateLockCategory(
        lockCategory?: RHProcurementTemplateLockCategory
    ) {
        const firstPart = this.partsEditorView._partEditorViews.firstElement
        if (lockCategory === "cpv") {
            firstPart?.cpvCodeTextField.focus()
        }
        else if (lockCategory === "requiredDocuments") {
            this.requiredDocumentsEditorView.focus()
        }
        else if (lockCategory === "invitedCompanies") {
            firstPart?.openInvitedCompaniesButton.focus()
        }
        else if (lockCategory === "publicationVisibility") {
            this.publicProcurementModeButton.focus()
        }
        else {
            this.partsEditorView._focusManager?.focusFirst()
        }
    }


    async navigationWillReset(): Promise<boolean> {
        if (!this._hasUnsavedData()) {
            return YES
        }
        return new Promise(resolve => {
            let objectName = "hanke"
            if (this._mode === "template") {
                objectName = "malli"
            }
            const shower = CBDialogViewShower.showQuestionDialogWithCloseButton(
                `Kas soovid ${objectName} salvestada?`,
                () => resolve(NO)
            )
            shower.dialogView.view.yesButton.titleLabel.text = "Jah"
            shower.dialogView.view.noButton.titleLabel.text = "Ei"
            shower.dialogView.view.noButtonDismissesDialog = NO
            shower.dialogView.view.setNeedsLayoutUpToRootView()

            shower.noButtonWasPressed = () => {
                this._isDirty = NO
                this._removeFormSessionSnapshot()
                resolve(YES)
                shower.dialogView.dismiss()
            }

            shower.yesButtonWasPressed = async () => {
                shower.dialogView.view.yesButton.enabled = NO
                shower.dialogView.view.noButton.enabled = NO
                shower.dialogView.view.closeButton.enabled = NO
                const savedProcurement = await this.saveDraft({ allowsIncompleteForm: YES })
                if (!savedProcurement) {
                    shower.dialogView.dismiss()
                    return
                }
                resolve(YES)
                shower.dialogView.dismiss()
            }
        })
    }


    _markDirty() {
        if (this._formStateIsBeingApplied) {
            return
        }
        this._isDirty = YES
        this._saveFormSessionSnapshot()
    }


    _saveFormSessionSnapshot() {
        const key = this._formSessionSnapshotKey()
        if (!key) {
            return
        }
        const procurement = {
            ...this._currentProcurementPayloadWithStatus("draft"),
            templateCreatedBy: this.class.procurement?.templateCreatedBy,
            summationLastCheck: null
        }
        RHFormSessionSnapshotStore.saveSnapshotForKey<NewProcurementFormSessionSnapshot>(
            {
                procurement,
                uploadedFiles: this.fileUploadView.uploadedFiles.map(uploadedFile => ({
                    ...uploadedFile
                })),
                requiredDocuments: this.requiredDocumentsEditorView.sessionSnapshot(),
                controls: {
                    autoExtendSelected: this.autoExtendProcurementCheckbox.selected,
                    lessThanOffersText: this.lessThanOffersTextField.text,
                    extendDeadlineDaysText: this.extendDeadlineDaysTextField.text,
                    allowMultipleOffersPerPartSelected:
                        this.allowMultipleOffersPerPartCheckbox.selected
                },
                basis: {
                    companyId: RHCore.sharedInstance.companyAffiliationStore.activeCompanyId!,
                    mode: this._basisMode,
                    sourceId: this._basisSourceId,
                    sourceTemplateId: this._sourceTemplateId,
                    sourceTemplateTitle: this._sourceTemplateTitle,
                    sourceTemplateLocks: this._sourceTemplateLocks,
                    shouldShowTeamFromSourceTemplate: this._shouldShowTeamFromSourceTemplate,
                    templateLocks: IF(this._mode === "template")(() => this._currentTemplateLocks()).ELSE(() => undefined),
                    expectedTemplateUpdatedAt: this._expectedTemplateUpdatedAt,
                    templateInstantiationKey: this._templateInstantiationKey
                }
            },
            key
        )
        const scope = this._formSessionSnapshotScope()
        if (scope) {
            RHFormSessionSnapshotStore.saveSnapshotKeyReferenceForScope(key, scope)
        }
    }


    _formSessionSnapshotKey(shouldUseNewProcurementScope = NO): string | undefined {
        if (this._isPublishedAmendmentMode) {
            return undefined
        }
        const userId = RHCore.sharedInstance.userProfile?._id?.toString() ?? ""
        if (userId.length === 0) {
            return undefined
        }

        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (!companyId) {
            return undefined
        }

        let draftIdentifier = "new"
        const routeProcurementId = UIRoute.currentRoute.componentWithViewController(
            NewProcurementViewController
        )?.parameters.id
        if (routeProcurementId && !shouldUseNewProcurementScope) {
            draftIdentifier = "draft:" + routeProcurementId
        }
        const sourceIdentifier = this._sourceTemplateId ?? this._basisSourceId ?? "no-source"
        const instantiationKey = this._templateInstantiationKey ?? "no-instantiation"
        const expectedVersion = this._expectedTemplateUpdatedAt ?? "no-version"
        return [
            "new-procurement-form-v2",
            userId,
            companyId,
            this._basisMode,
            sourceIdentifier,
            instantiationKey,
            expectedVersion,
            draftIdentifier
        ].join(":")
    }


    _formSessionSnapshot(): NewProcurementFormSessionSnapshot | undefined {
        const key = this._formSessionSnapshotKey()
        if (!key) {
            return undefined
        }
        const directSnapshot = RHFormSessionSnapshotStore
            .snapshotForKey<NewProcurementFormSessionSnapshot>(key)
        if (directSnapshot) {
            return directSnapshot
        }
        const scope = this._formSessionSnapshotScope()
        const referencedKey = scope
            ? RHFormSessionSnapshotStore.snapshotKeyReferencedByScope(scope)
            : undefined
        if (!referencedKey || !referencedKey.startsWith("new-procurement-form-v2:")) {
            return undefined
        }
        return RHFormSessionSnapshotStore
            .snapshotForKey<NewProcurementFormSessionSnapshot>(referencedKey)
    }


    _removeFormSessionSnapshot(shouldUseNewProcurementScope = NO) {
        const key = this._formSessionSnapshotKey(shouldUseNewProcurementScope)
        if (key) {
            RHFormSessionSnapshotStore.removeSnapshotForKey(key)
        }
        const scope = this._formSessionSnapshotScope(shouldUseNewProcurementScope)
        if (scope) {
            const referencedKey = RHFormSessionSnapshotStore.snapshotKeyReferencedByScope(scope)
            if (referencedKey && referencedKey !== key) {
                RHFormSessionSnapshotStore.removeSnapshotForKey(referencedKey)
            }
            RHFormSessionSnapshotStore.removeSnapshotKeyReferenceForScope(scope)
        }
    }


    _formSessionSnapshotScope(shouldUseNewProcurementScope = NO): string | undefined {
        const userId = RHCore.sharedInstance.userProfile?._id?.toString() ?? ""
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (!userId || !companyId) {
            return undefined
        }
        const route = UIRoute.currentRoute.componentWithViewController(NewProcurementViewController)
        let routeIdentity = "new"
        if (!shouldUseNewProcurementScope) {
            routeIdentity = route?.parameters.id ?? route?.parameters.templateId ?? "new"
        }
        return ["new-procurement-form-v2", userId, companyId, routeIdentity].join(":")
    }


    _hasUnsavedData(): boolean {
        return this._isDirty
    }


    _requestBasisSwitch(mode: RHProcurementSourceSelectionMode) {
        const switchBasis = () => {
            this._removeFormSessionSnapshot()
            const generation = ++this._basisGeneration
            this._clearBasisMetadata()
            this.clearAllFields()
            if (mode === "template") {
                this._basisMode = "template"
            }
            else {
                this._basisMode = "copy"
            }
            const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
            if (!companyId) {
                CBDialogViewShower.alert("Ettevõtte konteksti ei leitud.")
                return
            }
            const dialog = new CopyProcurementDialogView()
            void dialog.show(mode, companyId, async selection => {
                if (generation !== this._basisGeneration) {
                    return
                }
                await this._applySourceSelection(selection, generation)
            })
        }

        if (!this._hasUnsavedData()) {
            switchBasis()
            return
        }

        const shower = CBDialogViewShower.showQuestionDialog(
            "Aluse vahetamine asendab vormi praeguse sisu. Kas soovid jätkata?",
            () => undefined
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Jah, vaheta alus"
        shower.dialogView.view.noButton.titleLabel.text = "Tühista"
        shower.dialogView.view.setNeedsLayoutUpToRootView()
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            switchBasis()
        }
    }


    async _applySourceSelection(
        selection: RHProcurementSourceSelectionResult,
        generation: number
    ) {
        if (generation !== this._basisGeneration) {
            return
        }
        if (selection.kind === "template" &&
            (selection.templateLocks.isContractingAuthorityTypeLocked ?? selection.templateLocks.isProcurementCategoryLocked)) {
            const company = RHCore.sharedInstance.companyAffiliationStore.activeCompanyAffiliation?.company ?? {}
            const authorityType = RHContractingAuthorityRules.typeForProcurementInCompany(selection.source, company)
            if (!RHContractingAuthorityRules.allowedTypesForCompany(company).includes(authorityType)) {
                CBDialogViewShower.alert("Malli lukustatud hankija määratlus ei ole ettevõttes enam lubatud. Uuenda esmalt malli.")
                return
            }
        }
        await this._applyProcurementCopySource(selection.source, generation)
        if (generation !== this._basisGeneration) {
            return
        }
        if (selection.kind === "template") {
            this._basisSourceId = selection.templateId
            this._sourceTemplateId = selection.templateId
            this._sourceTemplateTitle = selection.source.templateName
            this._sourceTemplateLocks = { ...selection.templateLocks }
            this._expectedTemplateUpdatedAt = selection.expectedTemplateUpdatedAt
            this._templateInstantiationKey = crypto.randomUUID()
            this._templateCapabilities = selection.capabilities
            this._basisMode = "template"
        }
        else {
            this._basisSourceId = selection.source.sourceProcurementId
            this._basisMode = "copy"
        }
        this._applyEditingRestrictionsToForm()
        this._updateViewsForFormMode()
        this.view.setNeedsLayoutUpToRootView()
        if (!this._formStateIsBeingApplied) {
            this._saveFormSessionSnapshot()
        }
    }


    _clearBasisMetadata() {
        this._sourceTemplateId = undefined
        this._sourceTemplateTitle = undefined
        this._shouldShowTeamFromSourceTemplate = YES
        this._basisSourceId = undefined
        this._sourceTemplateLocks = undefined
        this._expectedTemplateUpdatedAt = undefined
        this._templateInstantiationKey = undefined
        this._templateCapabilities = undefined
        this.requiredDocumentsEditorView.readOnly = NO
        this.partsEditorView.configureTemplateProtection(undefined)
        this._applyEditingRestrictionsToForm()
        this._applyTemplateAuthoringLockControls()
    }


    async prepareForNewProcurement(): Promise<boolean> {
        const routeLink = UIRoute.currentRoute.linkRepresentation
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (this._formHydrationPromise) {
            await this._formHydrationPromise
        }
        if (routeLink !== UIRoute.currentRoute.linkRepresentation ||
            companyId !== RHCore.sharedInstance.companyAffiliationStore.activeCompanyId) {
            return NO
        }

        this._removeFormSessionSnapshot()
        ++this._basisGeneration
        this._formStateIsBeingApplied = YES
        try {
            this._clearBasisMetadata()
            this._mode = "procurement"
            this._basisMode = "blank"
            this.clearAllFields()
            this._removeFormSessionSnapshot(YES)
        }
        finally {
            this._formStateIsBeingApplied = NO
        }
        return YES
    }


    async _startFromBlankForm() {
        if (!await this.prepareForNewProcurement()) {
            return
        }
        UIRoute.currentRoute
            .routeByRemovingParameterInComponent(
                NewProcurementViewController.routeComponentName,
                NewProcurementViewController.ParameterIdentifierName.templateId
            )
            .applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
    }


    get _templateIdForContractReport(): string | undefined {
        if (this._mode === "template") {
            return this.class.procurement?._id?.toString()
        }
        return this._sourceTemplateId ?? referenceId(this.class.procurement?.sourceTemplate)
    }


    get _hasUnsavedTemplateRegistryFields(): boolean {
        const procurement = this.class.procurement
        return this._mode === "template" && (
            !procurement?._id ||
            (procurement.registryReferenceNumber ?? "") !== this.registryReferenceTextField.text.trim() ||
            (procurement.registryPartNumber ?? "") !== this.registryPartTextField.text.trim()
        )
    }


    _updateTemplateContractExportAvailability() {
        this.templateContractReportView.exportButton.enabled = this._isTemplateContractExportAllowed &&
            !this._hasUnsavedTemplateRegistryFields && !this._sourceTemplateActionIsPending
        this.templateContractReportView.exportHelpLabel.removeFromSuperview()
        if (this._hasUnsavedTemplateRegistryFields) {
            this.templateContractReportView.exportHelpLabel.text =
                "XLSX ekspordiks salvesta malli registriandmete muudatused. Osanumber võib puududa."
            this.templateContractReportView.exportHelpLabel.addedAsSubviewToView(this.templateContractReportView)
        }
    }


    async _exportTemplateUsageForId(templateId?: string) {
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (!companyId || !templateId || this._sourceTemplateActionIsPending) {
            return
        }
        if (this._hasUnsavedTemplateRegistryFields) {
            CBDialogViewShower.alert(
                "XLSX ekspordiks salvesta malli registriandmete muudatused. Osanumber võib puududa."
            )
            return
        }
        this._sourceTemplateActionIsPending = YES
        this.sourceTemplateActionsButton.enabled = NO
        this._updateTemplateContractExportAvailability()
        try {
            const result = await RHCore.sharedInstance.procurementStore
                .exportProcurementTemplateUsageXLSX({
                    companyId, templateId,
                    procurementId: this.class.procurement?._id?.toString(),
                    contractingAuthorityType: this._selectedContractingAuthorityType,
                    referenceDate: this._latestCPVSummationReport?.referenceDate
                })
            if (!result.success) {
                this._showSourceTemplateActionError(result.errorCode)
                return
            }
            ProcurementExcelExportDownloader.download(result.value)
            if (result.value.warnings.length > 0) {
                CBDialogViewShower.alert(
                    `XLSX loodi, kuid ${result.value.warnings.length} rea andmed vajavad kontrollimist.`
                )
            }
        }
        catch (error) {
            console.error("NewProcurementViewController: template XLSX export failed", error)
            CBDialogViewShower.alert("XLSX loomine ebaõnnestus.")
        }
        finally {
            this._sourceTemplateActionIsPending = NO
            this.sourceTemplateActionsButton.enabled = YES
            this._updateTemplateContractExportAvailability()
        }
    }


    async _copySourceTemplateLink() {
        const templateId = this._sourceTemplateId
        if (!templateId || this._sourceTemplateActionIsPending) {
            return
        }
        this._sourceTemplateActionIsPending = YES
        this._syncSourceTemplateActions()
        try {
            const route = UIRoute.currentRoute
                .routeByRemovingComponentsOtherThanOnesNamed([])
                .routeWithViewControllerComponent(NewProcurementViewController, { templateId })
            const baseUrl = ("" + window.location.href).split("#")[0]
            const didCopy = await RHClipboard.copy(baseUrl + route.linkRepresentation)
            if (didCopy) {
                CBDialogViewShower.alert("Hankemalli link kopeeriti.")
            }
            else {
                CBDialogViewShower.alert("Hankemalli lingi kopeerimine ebaõnnestus.")
            }
        }
        finally {
            this._sourceTemplateActionIsPending = NO
            this._syncSourceTemplateActions()
        }
    }


    async _editSourceTemplate() {
        const templateId = this._sourceTemplateId
        if (!templateId || this._sourceTemplateActionIsPending) {
            return
        }
        if (this._hasUnsavedData()) {
            const canLeaveSourceForm = await this.navigationWillReset()
            if (!canLeaveSourceForm) {
                return
            }
        }
        this._sourceTemplateActionIsPending = YES
        this._syncSourceTemplateActions()
        try {
            this._removeFormSessionSnapshot()
            this._isDirty = NO
            ++this._basisGeneration
            this._clearBasisMetadata()
            this._basisMode = "blank"
            UIRoute.currentRoute
                .routeByRemovingParameterInComponent(
                    NewProcurementViewController.routeComponentName,
                    NewProcurementViewController.ParameterIdentifierName.templateId
                )
                .routeBySettingParameterInComponent(
                    NewProcurementViewController.routeComponentName,
                    NewProcurementViewController.ParameterIdentifierName.id,
                    templateId
                )
                .applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
            await this._loadProcurementIfPossible()
            this.view.setNeedsLayoutUpToRootView()
        }
        finally {
            this._sourceTemplateActionIsPending = NO
            if (this._sourceTemplateId) {
                this._syncSourceTemplateActions()
            }
        }
    }


    _confirmArchiveSourceTemplate() {
        if (!this._sourceTemplateId || this._sourceTemplateActionIsPending) {
            return
        }
        const shower = CBDialogViewShower.showQuestionDialogWithSupportingText(
            "Kas soovid hankemalli arhiveerida?",
            "Mall kaob aktiivsest valikust. Selle põhjal juba loodud hanked ei muutu ja kasutusstatistika säilib."
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Arhiveeri"
        shower.dialogView.view.noButton.titleLabel.text = "Sulge"
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            void this._runSourceTemplateMutation("archive")
        }
    }


    _confirmDeleteSourceTemplate() {
        if (!this._sourceTemplateId || this._sourceTemplateActionIsPending) {
            return
        }
        const shower = CBDialogViewShower.showQuestionDialogWithSupportingText(
            "Kas soovid hankemalli kustutada?",
            "Kasutamata hankemall kustutatakse jäädavalt. Kui malli on vahepeal kasutatud, võib server kustutamisest keelduda."
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Kustuta"
        shower.dialogView.view.noButton.titleLabel.text = "Sulge"
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            void this._runSourceTemplateMutation("delete")
        }
    }


    async _runSourceTemplateMutation(action: "archive" | "restore" | "delete") {
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const templateId = this._sourceTemplateId
        const expectedTemplateUpdatedAt = this._expectedTemplateUpdatedAt
        if (
            !companyId ||
            !templateId ||
            !expectedTemplateUpdatedAt ||
            this._sourceTemplateActionIsPending
        ) {
            return
        }
        this._sourceTemplateActionIsPending = YES
        this._syncSourceTemplateActions()
        try {
            let result: Awaited<ReturnType<
                typeof RHCore.sharedInstance.procurementStore.archiveProcurementTemplate
            >> | Awaited<ReturnType<
                typeof RHCore.sharedInstance.procurementStore.deleteUnusedProcurementTemplate
            >>
            if (action === "archive") {
                result = await RHCore.sharedInstance.procurementStore.archiveProcurementTemplate({
                    companyId,
                    templateId,
                    expectedTemplateUpdatedAt
                })
            }
            else if (action === "restore") {
                result = await RHCore.sharedInstance.procurementStore.restoreProcurementTemplate({
                    companyId,
                    templateId,
                    expectedTemplateUpdatedAt
                })
            }
            else {
                result = await RHCore.sharedInstance.procurementStore.deleteUnusedProcurementTemplate({
                    companyId,
                    templateId,
                    expectedTemplateUpdatedAt
                })
            }
            if (!result.success) {
                this._showSourceTemplateActionError(result.errorCode)
                return
            }
            this._removeFormSessionSnapshot()
            this._isDirty = NO
            const routeComponentNameToRemove =
                this.hostRouteComponentName ?? NewProcurementViewController.routeComponentName
            UIRoute.currentRoute
                .routeByRemovingComponentNamed(routeComponentNameToRemove)
                .routeWithComponent(UserMainViewController.routeComponentName, {
                    [UserMainViewController.ParameterIdentifierName.tab]: "drafts"
                })
                .apply()
        }
        catch (error) {
            console.error("NewProcurementViewController: template mutation failed", error)
            CBDialogViewShower.alert("Hankemalli toiming ebaõnnestus.")
        }
        finally {
            this._sourceTemplateActionIsPending = NO
            if (this._sourceTemplateId) {
                this._syncSourceTemplateActions()
            }
        }
    }


    _showSourceTemplateActionError(errorCode?: string) {
        if (errorCode === "TEMPLATE_VERSION_CONFLICT") {
            CBDialogViewShower.alert("Hankemall on vahepeal muutunud. Värskenda vorm enne jätkamist.")
            return
        }
        if (errorCode === "TEMPLATE_ALREADY_USED") {
            CBDialogViewShower.alert("Kasutatud hankemalli ei saa kustutada. Arhiveeri mall.")
            return
        }
        if (errorCode === "NO_PUBLISHED_TEMPLATE_USAGE") {
            CBDialogViewShower.alert("Malli põhjal pole veel avaldatud hankeid.")
            return
        }
        CBDialogViewShower.alert("Hankemalli toiming ebaõnnestus.")
    }


    // Entry point for DraftProcurementsViewController's "Kasuta malli" action
    // (and any future "open new tab pre-filled from X" caller) — reads the
    // one-shot copySourceId route parameter instead of taking the source
    // directly, since the caller lives on a different tab of the same
    // ProcurerMainViewController and has no direct reference to this instance.
    async _applyCopySourceFromRoute(copySourceId: string) {
        try {
            const copySource = await RHCore.sharedInstance.procurementStore.retrieveProcurementCopySource(copySourceId)
            if (
                copySource.procuringCompanyId &&
                copySource.procuringCompanyId !== RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
            ) {
                throw new Error("Copy source does not belong to the active company.")
            }
            await this._applyProcurementCopySource(copySource)
        }
        catch (error) {
            console.error("NewProcurementViewController: failed to apply copy source from route:", error)
            CBDialogViewShower.alert("Malli laadimine ebaõnnestus.")
        }
    }


    async _applyTemplateFromRoute(templateId: string) {
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (!companyId) {
            CBDialogViewShower.alert("Ettevõtte konteksti ei leitud.")
            return
        }
        const generation = ++this._basisGeneration
        this._clearBasisMetadata()
        this._basisMode = "template"
        try {
            const result = await RHCore.sharedInstance.procurementStore.resolveProcurementSourceSelection({
                mode: "template",
                sourceId: templateId,
                companyId
            })
            if (!result.success) {
                const message = result.errorCode === "TEMPLATE_ARCHIVED"
                    ? "Hankemall on arhiveeritud."
                    : "Hankemalli laadimine ebaõnnestus."
                CBDialogViewShower.alert(message)
                return
            }
            await this._applySourceSelection(result.value, generation)
        }
        catch (error) {
            console.error("NewProcurementViewController: failed to apply template route", error)
            CBDialogViewShower.alert("Hankemalli laadimine ebaõnnestus.")
        }
    }


    async _applyProcurementCopySource(
        copySource: RHProcurementCopySource,
        expectedBasisGeneration?: number
    ): Promise<void> {
        if (
            expectedBasisGeneration !== undefined &&
            expectedBasisGeneration !== this._basisGeneration
        ) {
            return
        }
        this.clearAllFields()

        NewProcurementViewController.procurement = undefined
        this._updatePublicationModeFromProcurement(copySource)
        this.procurementTitleTextField.text = copySource.title || ""

        if (copySource.minimumAcceptableNumberOfOffers && copySource.minimumAcceptableNumberOfOffers > 0) {
            this.autoExtendProcurementCheckbox.selected = YES
            this.lessThanOffersTextField.enabled = YES
            this.extendDeadlineDaysTextField.enabled = YES
            this.lessThanOffersTextField.text = copySource.minimumAcceptableNumberOfOffers.toString()
            this.extendDeadlineDaysTextField.text =
                copySource.numberOfDaysToExtendDeadlineByIfTooFewOffers?.toString() || ""
        }
        else {
            this.autoExtendProcurementCheckbox.selected = NO
            this.lessThanOffersTextField.enabled = NO
            this.extendDeadlineDaysTextField.enabled = NO
            this.lessThanOffersTextField.text = ""
            this.extendDeadlineDaysTextField.text = ""
        }

        this.submissionDeadlineDatePicker.clear()
        const company = RHCore.sharedInstance.companyAffiliationStore.activeCompanyAffiliation?.company ?? {}
        let copiedContractingAuthorityType: RHContractingAuthorityType | undefined
        // Published legacy sources can predate the authority-type field. When the
        // target company has several allowed types, leave the new form unselected
        // for the user instead of rejecting the copy or guessing a value.
        if (
            copySource.contractingAuthorityType ||
            copySource.isCompetition
        ) {
            copiedContractingAuthorityType = RHContractingAuthorityRules.typeForProcurementInCompany(
                copySource,
                company
            )
        }
        this._updateContractingAuthorityChoicesWithSelection(
            copiedContractingAuthorityType
        )
        if (copySource.sourceKind === "template") {
            this.registryReferenceTextField.text = copySource.registryReferenceNumber ?? ""
            this.registryPartTextField.text = copySource.registryPartNumber ?? ""
            this._resetRegistryLookup()
            if (copySource.deadlineDate) {
                this.submissionDeadlineDatePicker.selectedDate = new Date(copySource.deadlineDate)
            }
        }

        this.allowMultipleOffersPerPartCheckbox.selected =
            typeof copySource.maxOffersPerPart === "number"

        this._updatePublishEstimatedCostValidationMessageVisibility(NO)
        this.publishEstimatedCostButtonGroup.value = ""
        if (copySource.publishEstimatedCostOfProcurement === YES) {
            this.publishEstimatedCostButtonGroup.value = "yes"
        }
        else if (copySource.publishEstimatedCostOfProcurement === NO) {
            this.publishEstimatedCostButtonGroup.value = "no"
        }

        const copiedParts = (copySource.parts ?? []) as Partial<RHProcurementPart>[]
        this.partsEditorView.procurementParts = copiedParts
        await this.partsEditorView.prepareForValidation()
        if (
            expectedBasisGeneration !== undefined &&
            expectedBasisGeneration !== this._basisGeneration
        ) {
            return
        }
        if (Array.isArray(copySource.invitedCompanies)) {
            this.partsEditorView.restoreInvitedCompanies(copySource.invitedCompanies)
        }
        this._updatePartsInRequiredDocumentsEditor()
        const requiredDocumentParts = copiedParts.map((part, index) => ({
            _id: this.partsEditorView.partIdentifiers[index]?._id,
            partNumber: part.partNumber ?? index + 1,
            requiredDocuments: part.requiredDocuments ?? []
        }))
        this.requiredDocumentsEditorView.loadFromParts(requiredDocumentParts)

        this.fileUploadView.clearFiles()
        if (copySource.sourceKind === "template") {
            this._shouldShowTeamFromSourceTemplate = copySource.shouldShowTeamInDerivedProcurements !== NO
            this.procurementTeamView.addsCurrentUserWhenMissing = YES
            this.procurementTeamView.resetToCurrentUser()
            const fileIds = (copySource.files ?? []).map(file => {
                if (typeof file === "object" && "_id" in file) {
                    return file._id.toString()
                }
                return file.toString()
            })
            if (fileIds.length > 0) {
                const response = await SocketClient.RetrieveFilesMetadata({
                    fileIds,
                    companyId: copySource.procuringCompanyId
                })
                if (expectedBasisGeneration !== undefined && expectedBasisGeneration !== this._basisGeneration) {
                    return
                }
                this.fileUploadView.shouldDeleteFilesWhenRemovedFromList = NO
                this.fileUploadView.initWithExistingFiles(response.result)
            }
        }
        this.updateCostSummary()
        this._updatePartTerminology()
        this._scheduleCPVSummationReport()
        this._markDirty()
        this.view.setNeedsLayoutUpToRootView()
        this.procurementTitleTextField.focus()
    }


    override async viewWillAppear() {
        const superViewWillAppearPromise = super.viewWillAppear()
        RHCore.sharedInstance.procurementStore.subscribe(this, () => {
            if (this._formAppearanceIsActive) {
                this._scheduleCPVSummationReport()
            }
        })

        // Assign the shared promise before the first await. UIViewController
        // starts viewDidAppear without awaiting this method, so assigning it
        // later would leave a window where validation can still run against
        // constructor-default form state.
        const formHydrationPromise = this._ensureFormHydratedForCurrentAppearance()
        await superViewWillAppearPromise
        await formHydrationPromise
    }


    _ensureFormHydratedForCurrentAppearance(): Promise<void> {
        if (!this._formAppearanceIsActive) {
            this._formAppearanceIsActive = YES
            const appearanceGeneration = ++this._formAppearanceGeneration
            this._didValidateFromRoute = NO
            this._didApplyCopySourceFromRoute = NO
            const previousHydrationPromise = this._formHydrationPromise
            this._formHydrationPromise = (async () => {
                // Serialise a fast leave/re-enter cycle. The previous load may
                // already be mutating the shared controller and cannot be
                // cancelled safely, so the new appearance reloads after it.
                if (previousHydrationPromise) {
                    await previousHydrationPromise.then(() => undefined, () => undefined)
                }
                if (!this._isCurrentFormAppearance(appearanceGeneration)) {
                    return
                }
                await this._hydrateFormForCurrentAppearance()
            })()
        }
        return this._formHydrationPromise!
    }


    _isCurrentFormAppearance(appearanceGeneration: number): boolean {
        return this._formAppearanceIsActive &&
            this._formAppearanceGeneration === appearanceGeneration
    }


    async _hydrateFormForCurrentAppearance() {
        this._formStateIsBeingApplied = YES
        try {
            // Registreerime halduri siin, et see oleks olemas kohe, kui kasutaja Tab-i vajutab
            RHPageFocusManager.shared.registerContentControllerManager(
                this._focusManager,
                { focusesFirstItem: NO }
            )

            this._updateViewsForFormMode()
            await this.procurementTeamView.loadCompanyUsers()
            this._allowsFormSessionRestoreAfterLoad = YES
            await this._loadProcurementIfPossible()
            const formSessionSnapshot = this._formSessionSnapshot()
            if (formSessionSnapshot && this._allowsFormSessionRestoreAfterLoad) {
                const currentCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
                if (formSessionSnapshot.basis.companyId !== currentCompanyId) {
                    return
                }
                if (formSessionSnapshot.basis.sourceTemplateId && currentCompanyId) {
                    const templateResult = await RHCore.sharedInstance.procurementStore
                        .retrieveProcurementTemplateForUse(
                            currentCompanyId,
                            formSessionSnapshot.basis.sourceTemplateId,
                            formSessionSnapshot.basis.expectedTemplateUpdatedAt
                        )
                    if (
                        !templateResult.success ||
                        templateResult.value.expectedTemplateUpdatedAt !==
                            formSessionSnapshot.basis.expectedTemplateUpdatedAt
                    ) {
                        CBDialogViewShower.alert(
                            "Hankemall on vahepeal muutunud. Ajutist vormiseisu ei taastatud."
                        )
                        return
                    }
                    const templateValue = templateResult.value
                    this._templateCapabilities = templateValue.capabilities
                    this._sourceTemplateTitle = templateValue.source.templateName
                    formSessionSnapshot.basis.sourceTemplateLocks = {
                        ...templateValue.templateLocks
                    }

                    const snapshotParts = formSessionSnapshot.procurement.parts
                    const snapshotHasSourceMarkers = snapshotParts.some(
                        part => !!part.sourceTemplatePartId
                    )
                    if (templateValue.templateLocks.isCpvLocked && !snapshotHasSourceMarkers) {
                        const restoredParts: typeof snapshotParts = []
                        for (const [partIndex, sourcePart] of templateValue.source.parts.entries()) {
                            const snapshotPart = snapshotParts[partIndex]
                            if (snapshotPart) {
                                restoredParts.push({
                                    ...snapshotPart,
                                    partNumber: sourcePart.partNumber,
                                    CPV: sourcePart.CPV,
                                    sourceTemplatePartId: sourcePart.sourceTemplatePartId
                                })
                            }
                            else {
                                restoredParts.push({
                                    ...sourcePart,
                                    _id: sourcePart.sourceTemplatePartId
                                })
                            }
                        }
                        formSessionSnapshot.procurement.parts = restoredParts
                    }
                }
                this._basisMode = formSessionSnapshot.basis.mode
                this._basisSourceId = formSessionSnapshot.basis.sourceId
                this._sourceTemplateId = formSessionSnapshot.basis.sourceTemplateId
                this._sourceTemplateTitle = formSessionSnapshot.basis.sourceTemplateTitle ??
                    this._sourceTemplateTitle
                this._sourceTemplateLocks = formSessionSnapshot.basis.sourceTemplateLocks
                if (this._mode === "template" && formSessionSnapshot.basis.templateLocks) {
                    this._templateLocks = { ...formSessionSnapshot.basis.templateLocks }
                }
                this._expectedTemplateUpdatedAt =
                    formSessionSnapshot.basis.expectedTemplateUpdatedAt
                this._templateInstantiationKey =
                    formSessionSnapshot.basis.templateInstantiationKey
                await this._applyProcurementToForm(
                    withoutNullFields<RHProcurementFormSource>(formSessionSnapshot.procurement),
                    formSessionSnapshot.uploadedFiles
                )
                this._shouldShowTeamFromSourceTemplate =
                    formSessionSnapshot.basis.shouldShowTeamFromSourceTemplate !== NO
                this.requiredDocumentsEditorView.restoreSessionSnapshot(
                    formSessionSnapshot.requiredDocuments
                )
                this._restoreSessionFormControls(formSessionSnapshot.controls)
                this._applyEditingRestrictionsToForm()
                this._updateViewsForFormMode()
                this.view.setNeedsLayoutUpToRootView()
                this._isDirty = YES
            }
        }
        finally {
            this._formStateIsBeingApplied = NO
        }
    }


    override viewDidReceiveBroadcastEvent(event: { name: string }) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.companyAffiliationsDidChange) {
            queueMicrotask(() => this._updateProcuringCompanyViews())
        }
        if (event.name === RHCore.broadcastEventName.activeCompanyDidChange) {
            // clearAllFields() below mutates the subview tree, which re-enters
            // UICore's broadcastEventInSubtree/forEachViewInSubtree walk that is
            // still delivering this very event — nesting can overflow the call
            // stack. Defer past that walk.
            queueMicrotask(() => void this._resetFormForActiveCompanyChange())
        }
    }


    // Reset the team so members of the previous company cannot leak into the new procurement.
    async _resetFormForActiveCompanyChange() {
        if (!this._formAppearanceIsActive) {
            return
        }
        ++this._basisGeneration
        this._clearBasisMetadata()
        this._basisCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        this._basisMode = "blank"
        this.clearAllFields()
        this.procurementTeamView.addsCurrentUserWhenMissing = YES
        this.procurementTeamView.resetToCurrentUser()
        await this.procurementTeamView.loadCompanyUsers()
        if (this._mode === "template") {
            await this.templateManagersView.loadCompanyUsers()
        }
        this._isDirty = NO
        this.view.setNeedsLayoutUpToRootView()
    }

    override async viewDidAppear() {
        const superViewDidAppearPromise = super.viewDidAppear()
        const appearanceGeneration = this._formAppearanceGeneration
        const formHydrationPromise = this._formHydrationPromise

        await superViewDidAppearPromise
        if (
            !formHydrationPromise ||
            !this._isCurrentFormAppearance(appearanceGeneration)
        ) {
            return
        }
        await formHydrationPromise
        if (!this._isCurrentFormAppearance(appearanceGeneration) || this._detachedFormSubviews.length) {
            return
        }

        const routeComponent = UIRoute.currentRoute.componentWithViewController(NewProcurementViewController)
        if (routeComponent?.parameters.validateOnOpen === "1" && !this._didValidateFromRoute) {
            this._didValidateFromRoute = YES
            const validationMessage = routeComponent.parameters.validationMessage
            if (validationMessage) {
                CBDialogViewShower.alert(NewProcurementViewController._decodedRouteMessage(validationMessage))
                return
            }
            await this.partsEditorView.prepareForValidation()
            if (!this._isCurrentFormAppearance(appearanceGeneration)) {
                return
            }
            if (!this._validateForSubmission()) {
                return
            }
        }

        if (routeComponent?.parameters.focusParts === "1") {
            this.partsEditorView._focusManager?.focusFirst()
        }
        else if (
            (this._mode === "template" || this._basisMode !== "blank") &&
            this.sourceTemplateActionsButton.superview !== this.view
        ) {
            this._focusManager.focusFirst()
        }

    }


    override async viewWillDisappear() {
        const superViewWillDisappearPromise = super.viewWillDisappear()
        RHCore.sharedInstance.procurementStore.unsubscribe(this)
        this._cancelCPVSummationReport()
        const appearanceWasActive = this._formAppearanceIsActive

        if (appearanceWasActive) {
            // Invalidate pending viewDidAppear continuations. Keep the promise
            // reference so a rapid re-entry can serialise its fresh reload
            // behind any old hydration that is already in progress.
            this._formAppearanceIsActive = NO
            this._formAppearanceGeneration += 1
            this._didValidateFromRoute = NO
        }

        await superViewWillDisappearPromise
        if (!appearanceWasActive) {
            return
        }

        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
    }


    async _loadProcurementIfPossible() {

        for (const formSubview of this._detachedFormSubviews) {
            formSubview.addedAsSubviewToView(this.view)
        }
        this._detachedFormSubviews = []

        const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const appearanceGeneration = this._formAppearanceGeneration
        if (this._basisCompanyId && this._basisCompanyId !== activeCompanyId) {
            ++this._basisGeneration
            this._clearBasisMetadata()
            this.clearAllFields()
            this._basisMode = "blank"
        }
        this._basisCompanyId = activeCompanyId
        const basisGeneration = this._basisGeneration

        this._updateViewsForFormMode()

        const routeComponent = UIRoute.currentRoute.componentWithViewController(NewProcurementViewController)
        const procurementID = routeComponent?.parameters.id
        const templateId = routeComponent?.parameters.templateId

        // Default assumption for this appearance — the branches below (an
        // existing procurement's canonical status, or an explicit templateMode
        // route param) override it when applicable.
        this._mode = "procurement"
        this._updateViewsForFormMode()

        if (templateId && !procurementID && !this._didApplyCopySourceFromRoute) {
            this._didApplyCopySourceFromRoute = YES
            await this._applyTemplateFromRoute(templateId)
            return
        }

        const copySourceId = routeComponent?.parameters.copySourceId
        if (copySourceId && !procurementID && !this._didApplyCopySourceFromRoute) {
            this._didApplyCopySourceFromRoute = YES
            const copySourceCompanyId = routeComponent?.parameters.copySourceCompanyId
            if (copySourceCompanyId && copySourceCompanyId !== RHCore.sharedInstance.companyAffiliationStore.activeCompanyId) {
                CBDialogViewShower.alert(
                    "Malli kasutamiseks vali esmalt ülemisest menüüst selle ettevõtte kontekst."
                )
                return
            }
            await this._applyCopySourceFromRoute(copySourceId)
            return
        }

        if (!procurementID && !copySourceId && routeComponent?.parameters.templateMode === "1") {
            const dashboard = await RHCore.sharedInstance.procurementStore
                .retrieveOwnProcurementDashboard(["draft", "template"])
            if (!dashboard.companyCapabilities.canCreateTemplate) {
                this.clearAllFields()
                this.nextButton.removeFromSuperview()
                this.saveButton.removeFromSuperview()
                CBDialogViewShower.alert("Sul puudub hankemalli loomise õigus.")
                return
            }
            this._mode = "template"
            this._basisMode = "blank"
            this._updateViewsForFormMode()
        }

        if (procurementID) {

            this._allowsFormSessionRestoreAfterLoad = NO
            this.clearAllFields()
            this.nextButton.removeFromSuperview()
            this.saveButton.removeFromSuperview()
            this.cancelAmendmentButton.removeFromSuperview()

            try {

                const isAmendmentMode = this._isPublishedAmendmentMode
                let procurement: RHProcurement
                let capabilities: RHProcurementCapabilities

                if (isAmendmentMode) {
                    // Check the public capability projection before requesting
                    // private mutation data. A user without edit access can
                    // still open the ordinary published-procurement details.
                    capabilities = await RHCore.sharedInstance.procurementStore
                        .retrieveProcurementCapabilities(procurementID)

                    if (capabilities.canEdit !== YES) {
                        this.clearAllFields()
                        CBDialogViewShower.alert(
                            "Sul puudub selle hanke muutmise õigus.",
                            () => this._routeBackToProcurementDetails()
                        )
                        return
                    }

                    // A mutation form must not inherit a possibly stale detail
                    // snapshot from a long-lived SPA page. The fresh server
                    // version supplies the concurrency token used at save time.
                    procurement = await RHCore.sharedInstance.procurementStore
                        .refreshProcurement(procurementID)
                }
                else {
                    [procurement, capabilities] = await Promise.all([
                        RHCore.sharedInstance.procurementStore.refreshProcurement(procurementID),
                        RHCore.sharedInstance.procurementStore
                            .retrieveProcurementCapabilities(procurementID)
                    ])
                }

                if (isAmendmentMode) {
                    if (procurement.status !== "published") {
                        CBDialogViewShower.alert(
                            "Muuta saab ainult avaldatud hanget.",
                            () => this._routeBackToProcurementDetails()
                        )
                        return
                    }
                    if (procurement.offersOpenedAt) {
                        CBDialogViewShower.alert(
                            "Pakkumused on juba avatud. Hanke andmeid ei saa enam muuta.",
                            () => this._routeBackToProcurementDetails()
                        )
                        return
                    }
                }
                else if (procurement.status !== "draft" && procurement.status !== "template") {
                    const routeComponentNameToRemove =
                        this.hostRouteComponentName ?? NewProcurementViewController.routeComponentName
                    const backToRouteComponentName = this.backToRouteComponentName
                        ?? (UIRoute.currentRoute.componentWithViewController(NewProcurementViewController)
                            ?.parameters.backTo as string | undefined)
                        ?? UserMainViewController.routeComponentName
                    if (procurement.status == "draft-cancelled") {
                        UIRoute.currentRoute
                            .routeByRemovingComponentNamed(routeComponentNameToRemove)
                            .routeWithComponent(backToRouteComponentName, {})
                            .applyByReplacingCurrentRouteInHistory()
                        return
                    }
                    const detailsParameters: Record<string, string> = { id: procurementID }
                    if (backToRouteComponentName !== UserMainViewController.routeComponentName) {
                        detailsParameters[ProcurementDetailsViewController.ParameterIdentifierName.backTo] =
                            backToRouteComponentName
                    }
                    UIRoute.currentRoute
                        .routeByRemovingComponentNamed(routeComponentNameToRemove)
                        .routeWithViewControllerComponent(ProcurementDetailsViewController, detailsParameters)
                        .applyByReplacingCurrentRouteInHistory()
                    return
                }

                if (
                    !isAmendmentMode &&
                    procurement.status === "draft" &&
                    capabilities?.canModifyDraft !== YES
                ) {
                    this.clearAllFields()
                    CBDialogViewShower.alert(
                        "Sul puudub selle ettevalmistamisel oleva hanke muutmise õigus.",
                        () => this._routeBackToProcurementPreview(procurementID)
                    )
                    return
                }

                this._mode = "procurement"
                this._basisMode = "edit-draft"
                if (procurement.status === "template") {
                    if (
                        procurement.templateLifecycleStatus !== "active" ||
                        capabilities?.canEditTemplate !== YES ||
                        !procurement.templateUpdatedAt
                    ) {
                        this.clearAllFields()
                        this.nextButton.removeFromSuperview()
                        this.saveButton.removeFromSuperview()
                        CBDialogViewShower.alert(
                            procurement.templateLifecycleStatus === "archived"
                                ? "Arhiveeritud hankemalli ei saa muuta."
                                : "Sul puudub hankemalli muutmise õigus."
                        )
                        return
                    }
                    this._mode = "template"
                    this._basisMode = "edit-template"
                    this._expectedTemplateUpdatedAt = new Date(
                        procurement.templateUpdatedAt
                    ).toISOString()
                    this._templateCapabilities = capabilities
                    const locks = procurement.templateLocks ?? {
                        isCpvLocked: NO,
                        isRequiredDocumentsLocked: NO,
                        isInvitedCompaniesLocked: NO
                    }
                    this._templateLocks = { ...locks }
                }
                else if (procurement.sourceTemplate) {
                    this._sourceTemplateLocks = procurement.sourceTemplateLocks
                    this._templateCapabilities = capabilities
                }
                this._updateViewsForFormMode()

                if (isAmendmentMode && procurement.reconciliationParentProcurement) {
                    if (!await this._loadIdentityForRoundAmendment(procurement)) {
                        return
                    }
                }
                await this._applyProcurementToForm(procurement)
                if (isAmendmentMode && typeof procurement.maxOffersPerPart === "number") {
                    const snapshot = await RHCore.sharedInstance.offerStore
                        .retrieveIncomingOffersSnapshotForProcurement(procurementID)
                    if (appearanceGeneration !== this._formAppearanceGeneration ||
                        basisGeneration !== this._basisGeneration) {
                        return
                    }
                    this._updateMultipleOffersLockForOffers(snapshot.offers)
                }
                this._applyEditingRestrictionsToForm()

                if (isAmendmentMode) {
                    this.changeExplanationTextArea.text = ""
                }

                this._isDirty = NO
                this._allowsFormSessionRestoreAfterLoad = YES
                this.nextButton.addedAsSubviewToView(this.view)
                this.nextButton.enabled = YES
                this._updateViewsForFormMode()

            }
            catch (error) {

                if (appearanceGeneration !== this._formAppearanceGeneration ||
                    basisGeneration !== this._basisGeneration) {
                    return
                }
                this.clearAllFields()
                this._detachedFormSubviews = this.view.subviews.slice()
                for (const formSubview of this._detachedFormSubviews) {
                    formSubview.removeFromSuperview()
                }
                console.error("Failed to load procurement data:", error)
                CBDialogViewShower.alert(
                    messageOfError(error) || "Hanke andmete laadimine ebaõnnestus."
                )

            }

        }
        else {

            this.clearAllFields()
            if (this.nextButton.superview !== this.view) {
                this.nextButton.addedAsSubviewToView(this.view)
            }
            this.nextButton.enabled = YES
            this._updateViewsForFormMode()
            if (this._isPublishedAmendmentMode) {
                CBDialogViewShower.alert("Muudatuse tegemiseks peab hange olema valitud.")
            }

        }

        if (this._mode === "template") {
            await this.templateManagersView.loadCompanyUsers()
        }

    }


    _updateMultipleOffersLockForOffers(
        offers: RHIncomingOffersContentSnapshot["offers"]
    ) {
        const submittedCompanyPartPairs = new Set<string>()
        this._hasMultipleSubmittedOffersPerPart = NO
        for (const offer of offers.filter(candidate => candidate.status === "submitted")) {
            const companyId = referenceId(offer.offererCompany)
            for (const partOffer of offer.partOffers) {
                if (typeof partOffer === "object" && "partId" in partOffer &&
                    !("isCancelled" in partOffer && partOffer.isCancelled) &&
                    !NewProcurementViewController.procurement?.closedWithoutContractPartIds?.includes(partOffer.partId.toString())) {
                    const companyPartPair = `${companyId}:${partOffer.partId}`
                    if (submittedCompanyPartPairs.has(companyPartPair)) {
                        this._hasMultipleSubmittedOffersPerPart = YES
                    }
                    submittedCompanyPartPairs.add(companyPartPair)
                }
            }
        }
        if (this._hasMultipleSubmittedOffersPerPart) {
            this.allowMultipleOffersPerPartCheckbox.selected = YES
            this.multipleOffersLockExplanationView.addedAsSubviewToView(this.view)
        }
        else {
            this.multipleOffersLockExplanationView.removeFromSuperview()
        }
        this.allowMultipleOffersPerPartCheckbox.enabled =
            !this._sourceTemplateLocks?.isMaxOffersPerPartLocked && !this._hasMultipleSubmittedOffersPerPart
        this.view.setNeedsLayoutUpToRootView()
    }


    async _loadIdentityForRoundAmendment(procurement: RHProcurement) {
        const appearanceGeneration = this._formAppearanceGeneration
        let rootInformation = await RHCore.sharedInstance.procurementStore.retrieveProcurementInformation(procurement._id + "")
        if (!this._isCurrentFormAppearance(appearanceGeneration)) {
            return NO
        }
        const ancestors = new Set<string>([procurement._id + ""])
        let roundNumber = 0
        let sourceRootPartId = ""
        let parentId = referenceId(rootInformation.procurement.reconciliationParentProcurement)
        while (parentId) {
            if (ancestors.has(parentId)) {
                throw new Error("1. vooru hanke andmed ei ole kättesaadavad.")
            }
            ancestors.add(parentId)
            sourceRootPartId = rootInformation.procurement.reconciliationParentPartId ?? ""
            rootInformation = await RHCore.sharedInstance.procurementStore.retrieveProcurementInformation(parentId)
            if (!this._isCurrentFormAppearance(appearanceGeneration)) {
                return NO
            }
            roundNumber += 1
            parentId = referenceId(rootInformation.procurement.reconciliationParentProcurement)
        }
        let label = ProcurementRoundContext.titleForRound({ roundNumber })
        const part = rootInformation.procurement.parts.find(candidate => candidate._id?.toString() === sourceRootPartId)
        if (part && rootInformation.procurement.parts.length > 1) {
            label = `Osa ${part.partNumber} · ${label}`
        }
        this._roundAmendmentIdentity = { rootId: rootInformation.procurement._id + "", title: rootInformation.procurement.title, label }
        this._updateViewsForFormMode()
        return YES
    }

    async _applyProcurementToForm(
        procurement: RHProcurementFormSource,
        uploadedFilesOverride?: UploadedFile[]
    ) {
        this._updatePublicationModeFromProcurement(procurement)
        this.templateNameTextField.text = procurement.templateName ?? ""
        this.procurementTitleTextField.configureWithObject({
            text: this._roundAmendmentIdentity?.title ?? procurement.title ?? "",
            enabled: !this._roundAmendmentIdentity
        })
        this.registryReferenceTextField.text = procurement.sourceTemplateRegistryReferenceNumber ??
            procurement.registryReferenceNumber ?? ""
        this.registryPartTextField.text = procurement.sourceTemplateRegistryPartNumber ?? procurement.registryPartNumber ?? ""
        this._resetRegistryLookup()
        void this._loadRegistryProcurement()

        if (
            (procurement.minimumAcceptableNumberOfOffers ?? 0) > 0 ||
            (procurement.numberOfDaysToExtendDeadlineByIfTooFewOffers ?? 0) > 0
        ) {
            this.autoExtendProcurementCheckbox.selected = YES
            this.lessThanOffersTextField.enabled = YES
            this.extendDeadlineDaysTextField.enabled = YES
            this.lessThanOffersTextField.text = (procurement.minimumAcceptableNumberOfOffers || "").toString()
            this.extendDeadlineDaysTextField.text =
                (procurement.numberOfDaysToExtendDeadlineByIfTooFewOffers || "").toString()
        }
        else {
            this.autoExtendProcurementCheckbox.selected = NO
            this.lessThanOffersTextField.enabled = NO
            this.extendDeadlineDaysTextField.enabled = NO
            this.lessThanOffersTextField.text = ""
            this.extendDeadlineDaysTextField.text = ""
        }

        this.submissionDeadlineDatePicker.selectedDate = undefined
        if (procurement.deadlineDate) {
            this.submissionDeadlineDatePicker.selectedDate = new Date(procurement.deadlineDate)
        }

        this.allowMultipleOffersPerPartCheckbox.selected =
            typeof procurement.maxOffersPerPart === "number"

        this._updatePublishEstimatedCostValidationMessageVisibility(NO)
        this.publishEstimatedCostButtonGroup.value = ""
        if (procurement.publishEstimatedCostOfProcurement === YES) {
            this.publishEstimatedCostButtonGroup.value = "yes"
        }
        else if (procurement.publishEstimatedCostOfProcurement === NO) {
            this.publishEstimatedCostButtonGroup.value = "no"
        }

        const procurementParts = (procurement.parts ?? []).map(part => {
            const editablePart = {
                ...part
            } as typeof part & { sourceTemplatePartId?: string }
            if (!editablePart.sourceTemplatePartId && procurement.sourceTemplate) {
                editablePart.sourceTemplatePartId = part._id?.toString()
            }
            return editablePart
        })
        this.partsEditorView.procurementParts = procurementParts
        await this.partsEditorView.prepareForValidation()
        if (Array.isArray(procurement.invitedCompanies)) {
            this.partsEditorView.restoreInvitedCompanies(procurement.invitedCompanies)
        }
        this.requiredDocumentsEditorView.loadFromParts(procurementParts)
        this._updatePartsInRequiredDocumentsEditor()
        this._updatePartTerminology()
        this.updateCostSummary()

        this.fileUploadView.clearFiles()
        let uploadedFiles = uploadedFilesOverride
        if (!uploadedFiles) {
            uploadedFiles = []
            const procurementFiles = procurement.files ?? []
            for (const file of procurementFiles) {
                const populatedFile = populatedValueOfReference(file)
                uploadedFiles.push({
                    id: populatedFile?._id?.toString() ?? file.toString(),
                    name: populatedFile?.name || "Tundmatu fail",
                    type: populatedFile?.type || "application/octet-stream",
                    size: populatedFile?.size || 0
                })
            }
        }
        if (uploadedFiles.length > 0) {
            this.fileUploadView.initWithExistingFiles(uploadedFiles)
        }

        this.class.procurement = procurement
        const company = RHCore.sharedInstance.companyAffiliationStore.activeCompanyAffiliation?.company ?? {}
        this._updateContractingAuthorityChoicesWithSelection(
            IF(!!procurement._id && this._mode !== "template")(() =>
                RHContractingAuthorityRules.typeForProcurementInCompany(procurement, company)
            ).ELSE(() => procurement.contractingAuthorityType)
        )
        this._shouldShowTeamFromSourceTemplate = procurement.shouldShowTeamFromSourceTemplate !== NO
        if (this._mode === "template") {
            this.class.procurement.templateCreatedBy = procurement.templateCreatedBy ?? procurement.primaryProcuringUser
            this._configureTemplateManagersForTemplate(procurement)
            this.showTeamInDerivedProcurementsCheckbox.selected = procurement.shouldShowTeamInDerivedProcurements !== NO
        }
        else {
            this.procurementTeamView.addsCurrentUserWhenMissing = !procurement.sourceTemplateLocks?.isTeamLocked &&
                !(this._isPublishedAmendmentMode && !!procurement.reconciliationParentProcurement)
            this.procurementTeamView.configureFromProcurement(procurement)
        }
        this._clearExpiredDraftDeadline()
        this._scheduleCPVSummationReport()
        this.view.setNeedsLayoutUpToRootView()
    }


    _configureTemplateManagersForTemplate(template?: Partial<Pick<
        RHProcurement, "templateCreatedBy" | "primaryProcuringUser" | "templateManagerUsers"
    >>) {
        const creator = template?.templateCreatedBy ?? template?.primaryProcuringUser ?? RHCore.sharedInstance.userProfile
        const creatorId = RHTeamUserReferenceResolver.userIdFromReference(creator)
        this.templateManagersView.configureRoleMatrixPresentation({
            visibleRoles: ["authorized"],
            editableRoles: ["authorized"],
            roleTitles: { authorized: "Lubatud malli muuta" },
            roleDescriptions: { authorized: "Liige võib seda hankemalli muuta." },
            roleColumnWidths: { authorized: 184 },
            canCurrentUserChangeOwnRole: YES,
            nonEditableUserIds: [creatorId]
        })
        this.templateManagersView.configureRoleAssignments({
            authorizedUsers: [creator, ...template?.templateManagerUsers ?? []]
        })
        this.templateManagersView.usersDataView.searchTextField.placeholderText = "Otsi ettevõtte liikmeid"
    }


    _restoreSessionFormControls(controls: NewProcurementFormSessionSnapshot["controls"]) {
        this.autoExtendProcurementCheckbox.selected = controls.autoExtendSelected
        this.lessThanOffersTextField.enabled = controls.autoExtendSelected
        this.extendDeadlineDaysTextField.enabled = controls.autoExtendSelected
        this.lessThanOffersTextField.text = controls.lessThanOffersText
        this.extendDeadlineDaysTextField.text = controls.extendDeadlineDaysText

        this.allowMultipleOffersPerPartCheckbox.selected =
            controls.allowMultipleOffersPerPartSelected
        this.view.setNeedsLayoutUpToRootView()
    }


    override async handleRoute(route: UIRoute) {

        super.handleRoute(route)
        this._updateViewsForFormMode()
        const inquiryComponent = route.componentWithName(NewProcurementViewController.routeComponentName)

    }


    _routeBackToProcurementDetails() {
        const routeComponentNameToRemove =
            this.hostRouteComponentName ?? NewProcurementViewController.routeComponentName
        const backToRouteComponentName = this.backToRouteComponentName
            ?? (UIRoute.currentRoute.componentWithViewController(NewProcurementViewController)
                ?.parameters.backTo as string | undefined)
            ?? UserMainViewController.routeComponentName

        const procurementId = (this.class.procurement?._id ?? UIRoute.currentRoute.componentWithViewController(
            NewProcurementViewController
        )?.parameters.id)?.toString()

        if (!procurementId) {
            UIRoute.currentRoute
                .routeByRemovingComponentNamed(routeComponentNameToRemove)
                .routeWithComponent(backToRouteComponentName, {})
                .apply()
            return
        }

        const returnParameters = UIRoute.currentRoute.componentWithViewController(NewProcurementViewController)?.parameters
        const detailsParameters: Record<string, string> = {
            id: this._roundAmendmentIdentity?.rootId ?? returnParameters?.returnProcurementId ?? procurementId
        }
        if (returnParameters?.returnPartId) {
            detailsParameters.part = returnParameters.returnPartId
        }
        if (returnParameters?.returnRoundId) {
            detailsParameters.round = returnParameters.returnRoundId
        }
        else if (this._roundAmendmentIdentity) {
            detailsParameters.round = procurementId
        }
        if (returnParameters?.returnTab) {
            detailsParameters.tab = returnParameters.returnTab
        }
        if (backToRouteComponentName !== UserMainViewController.routeComponentName) {
            detailsParameters[ProcurementDetailsViewController.ParameterIdentifierName.backTo] =
                backToRouteComponentName
        }

        UIRoute.currentRoute
            .routeByRemovingComponentNamed(routeComponentNameToRemove)
            .routeWithViewControllerComponent(ProcurementDetailsViewController, detailsParameters)
            .apply()
    }


    _routeBackToProcurementPreview(procurementId: string) {
        const routeComponentNameToRemove =
            this.hostRouteComponentName ?? NewProcurementViewController.routeComponentName
        const backToRouteComponentName = this.backToRouteComponentName
            ?? (UIRoute.currentRoute.componentWithViewController(NewProcurementViewController)
                ?.parameters.backTo as string | undefined)
            ?? UserMainViewController.routeComponentName
        const previewParameters: Record<string, string> = { id: procurementId }
        if (backToRouteComponentName !== UserMainViewController.routeComponentName) {
            previewParameters[NewProcurementPreviewViewController.ParameterIdentifierName.backTo] =
                backToRouteComponentName
        }
        UIRoute.currentRoute
            .routeByRemovingComponentNamed(routeComponentNameToRemove)
            .routeWithViewControllerComponent(NewProcurementPreviewViewController, previewParameters)
            .apply()
    }


    clearAllFields() {

        this._roundAmendmentIdentity = undefined
        this.procurementTitleTextField.enabled = YES

        if (!this._formStateIsBeingApplied) {
            this._removeFormSessionSnapshot()
        }

        // Reset Text Fields
        this.procurementTitleTextField.text = ""
        this.templateNameTextField.text = ""
        this._updateInlineValidationMessage(this.procurementTitleValidationMessageLabel)
        this.contractingAuthorityButtonGroup.value = ""
        this.registryReferenceTextField.text = ""
        this.registryPartTextField.text = ""
        this._resetRegistryLookup()
        this.lessThanOffersTextField.text = ""
        this.extendDeadlineDaysTextField.text = ""
        this.changeExplanationTextArea.text = ""

        // Reset Checkboxes
        this.autoExtendProcurementCheckbox.selected = NO
        this.allowMultipleOffersPerPartCheckbox.selected = NO
        this._hasMultipleSubmittedOffersPerPart = NO
        this.multipleOffersLockExplanationView.removeFromSuperview()
        this._updatePublishEstimatedCostValidationMessageVisibility(NO)
        this.publishEstimatedCostButtonGroup.value = ""

        // Reset conditional field states
        this.lessThanOffersTextField.enabled = NO
        this.extendDeadlineDaysTextField.enabled = NO

        // Reset conditional publication state
        this._updatePublicationModeFromProcurement()

        // Clear uploaded files
        this.fileUploadView.clearFiles()

        // Reset parts to single empty part
        this.partsEditorView.procurementParts = []
        this._updatePartsInRequiredDocumentsEditor()
        this._updatePartTerminology()

        // Clear required documents
        this.requiredDocumentsEditorView.clear()

        this.procurementTeamView.resetToCurrentUser()
        this._configureTemplateManagersForTemplate()
        this.showTeamInDerivedProcurementsCheckbox.selected = YES

        // Hard reset: the submission deadline and the derived cost summary are not
        // covered by the field resets above, so clear them explicitly — otherwise a
        // stale deadline / "Eeldatav kogumaksumus" total lingers after a reset.
        this.submissionDeadlineDatePicker.clear()
        this._updateInlineValidationMessage(this.submissionDeadlineValidationMessageLabel)
        this.updateCostSummary()
        this._cancelCPVSummationReport()
        this._updateCPVSummationReport(undefined)

        // Reset the static procurement property
        NewProcurementViewController.procurement = undefined

        this._isDirty = NO
        this._updateViewsForFormMode()
        for (const field of [
            this.procurementTitleTextField,
            this.templateNameTextField,
            this.lessThanOffersTextField,
            this.extendDeadlineDaysTextField
        ]) {
            field.resetValidationState()
        }
        this.view.setNeedsLayoutUpToRootView()

    }


    override updateViewStyles() {
        super.updateViewStyles()
    }


    override layoutViewSubviews() {

        super.layoutViewSubviews()

        if (this._detachedFormSubviews.length) {
            return
        }

        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const fieldHeight = RHForms.fieldSingleLineFieldHeight

        // View bounds
        const bounds = this.view.contentBoundsWithInset(RHSpacing.xLarge)
            .rectangleWithMaxWidth(RHNewProcurementViewTokens.contentMaxWidth, 0.5)

        const autoExtendRuleWidths = {
            checkbox: RHForms.checkboxControlLeadingInset + RHForms.checkboxControlSize + RHForms.checkboxControlGap
                + this.autoExtendProcurementCheckbox.titleLabel.intrinsicContentWidth(fieldHeight) + RHSpacing.small,
            middleLabel: this.autoExtendProcurementMiddleLabel.intrinsicContentWidth(fieldHeight) + RHSpacing.small,
            endLabel: this.autoExtendProcurementEndLabel.intrinsicContentWidth(fieldHeight) + RHSpacing.small
        }
        const shouldWrapAutoExtendRule = bounds.width < autoExtendRuleWidths.checkbox
            + autoExtendRuleWidths.middleLabel + autoExtendRuleWidths.endLabel
            + RHNewProcurementViewTokens.autoExtendRuleFieldWidth * 2 + RHSpacing.small * 4
            + IF(this._templateLockButtons.deadlineExtension.superview === this.view)(() =>
                RHSpacing.small + this._templateLockButtons.deadlineExtension.widthIncludingVisibilityButton
            ).ELSE(() => 0)

        const visibleBasisControls = [
            this.copyPreviousProcurementButton,
            this.startFromTemplateButton,
            this.sourceTemplateActionsButton
        ].filter(control => control.superview === this.view)
        const basisControlWidths = visibleBasisControls.map(control => {
            if (control === this.sourceTemplateActionsButton) {
                return RHNewProcurementViewTokens.templateSourceActions.textButtonMaxWidth
            }
            const button = control as RHButton
            return Math.min(
                button.titleLabel.intrinsicContentWidth(
                    RHNewProcurementViewTokens.templateSourceActions.height
                ) + RHSpacing.medium * 2,
                RHNewProcurementViewTokens.copyPreviousProcurementButtonMaxWidth
            )
        })

        // Header
        bounds
            .rectangleWithHeight(Math.max(
                this.titleLabel.intrinsicContentHeight(bounds.width),
                RHNewProcurementViewTokens.templateSourceActions.height
            ))
            .performingFunctionWithSelf(headerRow => {
                headerRow
                    .IF(visibleBasisControls.length > 0)
                    .distributeViewsAlongWidth(
                        [this.titleLabel, ...visibleBasisControls],
                        [1, ...visibleBasisControls.map(() => nil)],
                        RHSpacing.small,
                        [nil, ...basisControlWidths]
                    )
                    .ELSE()
                    .assignedAsFrameOfView(this.titleLabel)
                    .ENDIF()
            })

            .IF(this.sourceTemplateExplanationLabel.superview === this.view)
            .rectangleForNextRow(RHSpacing.small, this.sourceTemplateExplanationLabel)
            .assignedAsFrameOfView(this.sourceTemplateExplanationLabel)
            .ENDIF()

            .IF(this.procuringCompanyLabel.superview === this.view)
            .rectangleForNextRow(padding, this.procuringCompanyLabel)
            .assignedAsFrameOfView(this.procuringCompanyLabel)
            .IF(this.procuringCompanyDropdown.superview === this.view)
            .rectangleForNextRow(RHSpacing.small, fieldHeight)
            .assignedAsFrameOfView(this.procuringCompanyDropdown)
            .ELSE()
            .rectangleForNextRow(RHSpacing.small, this.procuringCompanyNameLabel)
            .assignedAsFrameOfView(this.procuringCompanyNameLabel)
            .ENDIF()
            .ENDIF()

            .IF(this.templateNameTextField.superview === this.view)
            .rectangleForNextRow(padding, this.templateNameLabel)
            .assignedAsFrameOfView(this.templateNameLabel)
            .rectangleForNextRow(RHSpacing.small, fieldHeight)
            .assignedAsFrameOfView(this.templateNameTextField)
            .ENDIF()

            .IF(!this.registryReferenceTextField.hidden)
            .rectangleForNextRow(padding, this.registryReferenceLabel)
            .assignedAsFrameOfView(this.registryReferenceLabel)
            .rectangleForNextRow(RHSpacing.small, fieldHeight)
            .assignedAsFrameOfView(this.registryReferenceTextField)
            .ENDIF()
            .IF(this.registryLoadingView.superview === this.view)
            .rectangleForNextRow(RHSpacing.small, fieldHeight)
            .assignedAsFrameOfView(this.registryLoadingView)
            .ENDIF()
            .IF(this.registryTitleBanner.superview === this.view)
            .rectangleForNextRow(RHSpacing.small, this.registryTitleBanner)
            .assignedAsFrameOfView(this.registryTitleBanner)
            .ENDIF()
            .IF(this.registryPartSelectionBanner.superview === this.view)
            .rectangleForNextRow(RHSpacing.small, this.registryPartSelectionBanner)
            .assignedAsFrameOfView(this.registryPartSelectionBanner)
            .ENDIF()
            .IF(this.registryPartLabel.superview === this.view)
            .rectangleForNextRow(padding, this.registryPartLabel)
            .assignedAsFrameOfView(this.registryPartLabel)
            .IF(this.registryLotDropdown.superview === this.view)
            .rectangleForNextRow(RHSpacing.small, this.registryLotDropdown)
            .assignedAsFrameOfView(this.registryLotDropdown)
            .ELSE()
            .rectangleForNextRow(RHSpacing.small, fieldHeight)
            .assignedAsFrameOfView(this.registryPartTextField)
            .ENDIF()
            .ENDIF()
            .IF(this.registryPartTitleBanner.superview === this.view)
            .rectangleForNextRow(RHSpacing.small, this.registryPartTitleBanner)
            .assignedAsFrameOfView(this.registryPartTitleBanner)
            .ENDIF()

            .IF(this.templateContractReportView.superview === this.view)
            .rectangleForNextRow(padding, this.templateContractReportView)
            .assignedAsFrameOfView(this.templateContractReportView)
            .ENDIF()

            // Procurement title
            .IF(this.procurementTitleTextField.superview === this.view)
            .rectangleForNextRow(padding, labelHeight)
            .performingFunctionWithSelf(rowFrame =>
                this._templateLockButtons.title.layoutBesideTitleLabelInFrame(this.procurementTitleLabel, rowFrame)
            )
            .rectangleForNextRow(padding * 0.25, fieldHeight)
            .assignedAsFrameOfView(this.procurementTitleTextField)
            .IF(this.procurementTitleValidationMessageLabel.superview === this.view)
            .rectangleForNextRow(RHSpacing.tiny, this.procurementTitleValidationMessageLabel)
            .assignedAsFrameOfView(this.procurementTitleValidationMessageLabel)
            .ENDIF()
            .ENDIF()

            .IF(this.contractingAuthorityTitleLabel.superview === this.view)
            .rectangleForNextRow(padding, labelHeight)
            .performingFunctionWithSelf(rowFrame =>
                this._templateLockButtons.contractingAuthorityType.layoutBesideTitleLabelInFrame(this.contractingAuthorityTitleLabel, rowFrame)
            )
            .rectangleForNextRow(RHSpacing.small, this.contractingAuthorityChoicesView)
            .assignedAsFrameOfView(this.contractingAuthorityChoicesView)
            .ENDIF()

            .IF(this.publicationModeTitleLabel.superview === this.view && !this.publicationModeTitleLabel.hidden)
            .rectangleForNextRow(padding, labelHeight)
            .IF(this._templateLockButtons.publicationVisibility.superview === this.view)
            .performingFunctionWithSelf(rowFrame =>
                this._templateLockButtons.publicationVisibility.layoutBesideTitleLabelInFrame(this.publicationModeTitleLabel, rowFrame)
            )
            .ELSE()
            .rectangleWithWidth(this.publicationModeTitleLabel.intrinsicContentWidth(labelHeight) + RHSpacing.tiny)
            .assignedAsFrameOfView(this.publicationModeTitleLabel)
            .ENDIF()
            .rectangleWithWidth(bounds.width)
            .rectangleForNextRow(padding * 0.25, labelHeight)
            .rectangleWithMaxWidth(RHNewProcurementViewTokens.publicationModeOptionsMaxWidth, 0)
            .distributeViewsEquallyAlongWidth(
                [this.publicProcurementModeButton, this.directedProcurementModeButton],
                padding
            )
            .rectangleWithX(bounds.x, 0)
            .rectangleWithWidth(bounds.width, 0)
            .ENDIF()

            // Parts
            .IF(this.cpvSummationErrorLabel.superview === this.view)
            .rectangleForNextRow(RHSpacing.small, this.cpvSummationErrorLabel)
            .assignedAsFrameOfView(this.cpvSummationErrorLabel)
            .ENDIF()
            .IF(this.partsEditorView.superview === this.view)
            .IF(this.partsSectionTitleLabel.superview == this.view)
            .rectangleForNextRow(padding * 2, this.partsSectionTitleLabel)
            .performingFunctionWithSelf(rowFrame =>
                this._templateLockButtons.parts.layoutBesideTitleLabelInFrame(this.partsSectionTitleLabel, rowFrame)
            )
            .rectangleForNextRow(RHProcurementWorkflowTitleTokens.contentTopGap, this.partsSectionExplanationLabel)
            .assignedAsFrameOfView(this.partsSectionExplanationLabel)
            .rectangleForNextRow(padding, this.partsEditorView)
            .ELSE()
            .rectangleForNextRow(padding * 2, this.partsEditorView)
            .ENDIF()
            .assignedAsFrameOfView(this.partsEditorView)
            .ENDIF()

            // Cost summary
            .IF(this.costSummaryView.superview === this.view)
            .rectangleForNextRow(padding, this.costSummaryView.intrinsicContentHeight(this.partsEditorView.frame.width))
            .assignedAsFrameOfView(this.costSummaryView)
            .ENDIF()

            // Estimated cost visibility
            .IF(this.publishEstimatedCostTitleLabel.superview === this.view)
            .rectangleForNextRow(padding, labelHeight)
            .performingFunctionWithSelf(rowFrame =>
                this._templateLockButtons.publishEstimatedCost.layoutBesideTitleLabelInFrame(this.publishEstimatedCostTitleLabel, rowFrame)
            )
            .rectangleForNextRow(padding * 0.25, labelHeight)
            .rectangleWithMaxWidth(RHNewProcurementViewTokens.estimatedCostOptionButtonsMaxWidth, 0)
            .distributeViewsEquallyAlongWidth(
                [this.publishEstimatedCostYesButton, this.publishEstimatedCostNoButton],
                padding
            )
            .rectangleWithX(bounds.x, 0)
            .rectangleWithWidth(bounds.width, 0)
            .IF(this.publishEstimatedCostValidationMessageLabel.superview === this.view)
            .rectangleForNextRow(RHSpacing.tiny, this.publishEstimatedCostValidationMessageLabel)
            .assignedAsFrameOfView(this.publishEstimatedCostValidationMessageLabel)
            .ENDIF()

            .ENDIF()

            // Required Documents section
            .IF(this.requiredDocumentsEditorView.superview === this.view)
            .rectangleForNextRow(padding * 2, this.requiredDocumentsEditorView)
            .assignedAsFrameOfView(this.requiredDocumentsEditorView)

            .ENDIF()

            // Multiple offers per tenderer
            .IF(this.allowMultipleOffersPerPartCheckbox.superview === this.view)
            .rectangleForNextRow(RHSpacing.large, RHNewProcurementViewTokens.multipleOffersFieldHeight)
            .performingFunctionWithSelf(rowFrame => {
                rowFrame
                    .IF(this._templateLockButtons.maxOffersPerPart.superview === this.view)
                    .rectangleWithWidth(Math.min(
                        Math.max(0, rowFrame.width - this._templateLockButtons.maxOffersPerPart.widthIncludingVisibilityButton
                            - RHNewProcurementViewTokens.templateLockControl.gap),
                        RHForms.checkboxControlLeadingInset
                        + RHForms.checkboxControlSize
                        + RHForms.checkboxControlGap
                        + this.allowMultipleOffersPerPartCheckbox.titleLabel.intrinsicContentWidth(
                            RHNewProcurementViewTokens.multipleOffersFieldHeight
                        )
                        + RHSpacing.medium
                    ))
                    .assignedAsFrameOfView(this.allowMultipleOffersPerPartCheckbox)
                    .rectangleForNextColumn(
                        RHNewProcurementViewTokens.templateLockControl.gap,
                        this._templateLockButtons.maxOffersPerPart.widthIncludingVisibilityButton
                    )
                    .rectangleWithHeight(RHNewProcurementViewTokens.templateLockControl.size, 0.5)
                    .performingFunctionWithSelf(frame => this._templateLockButtons.maxOffersPerPart.layoutInFrame(frame))
                    .ELSE()
                    .assignedAsFrameOfView(this.allowMultipleOffersPerPartCheckbox)
                    .ENDIF()
            })

            .ENDIF()

            // Submission deadline
            .IF(this.multipleOffersLockExplanationView.superview === this.view)
            .rectangleForNextRow(RHSpacing.small, this.multipleOffersLockExplanationView)
            .assignedAsFrameOfView(this.multipleOffersLockExplanationView)
            .ENDIF()

            .IF(this.submissionDeadlineTitleLabel.superview === this.view)
            .rectangleForNextRow(padding, labelHeight)
            .performingFunctionWithSelf(rowFrame =>
                this._templateLockButtons.deadlineDate.layoutBesideTitleLabelInFrame(this.submissionDeadlineTitleLabel, rowFrame)
            )
            .rectangleForNextRow(padding * 0.25, fieldHeight)
            .assignedAsFrameOfView(this.submissionDeadlineDatePicker)
            .IF(this.submissionDeadlineValidationMessageLabel.superview === this.view)
            .rectangleForNextRow(RHSpacing.tiny, this.submissionDeadlineValidationMessageLabel)
            .assignedAsFrameOfView(this.submissionDeadlineValidationMessageLabel)
            .ENDIF()

            .ENDIF()

            // Auto-extend rule
            .IF(this.autoExtendProcurementCheckbox.superview === this.view)
            .rectangleForNextRow(padding, IF(shouldWrapAutoExtendRule)(() =>
                fieldHeight * 2 + RHSpacing.small
            ).ELSE(() => fieldHeight))
            .performingFunctionWithSelf(autoExtendRuleRow => {
                autoExtendRuleRow
                    .rectangleWithHeight(fieldHeight)
                    .rectangleWithWidth(autoExtendRuleWidths.checkbox)
                    .assignedAsFrameOfView(this.autoExtendProcurementCheckbox)
                    .IF(shouldWrapAutoExtendRule)
                    .rectangleForNextRow(RHSpacing.small, fieldHeight)
                    .rectangleWithX(autoExtendRuleRow.x)
                    .rectangleWithWidth(RHNewProcurementViewTokens.autoExtendRuleFieldWidth)
                    .ELSE()
                    .rectangleForNextColumn(RHSpacing.small, RHNewProcurementViewTokens.autoExtendRuleFieldWidth)
                    .ENDIF()
                    .performingFunctionWithSelf(frame => {
                        frame.rectangleWithHeight(RHNewProcurementViewTokens.autoExtendRuleFieldHeight, 0.5)
                            .assignedAsFrameOfView(this.lessThanOffersTextField)
                    })
                    .rectangleForNextColumn(
                        RHSpacing.small,
                        autoExtendRuleWidths.middleLabel
                    )
                    .assignedAsFrameOfView(this.autoExtendProcurementMiddleLabel)
                    .rectangleForNextColumn(RHSpacing.small, RHNewProcurementViewTokens.autoExtendRuleFieldWidth)
                    .performingFunctionWithSelf(frame => {
                        frame.rectangleWithHeight(RHNewProcurementViewTokens.autoExtendRuleFieldHeight, 0.5)
                            .assignedAsFrameOfView(this.extendDeadlineDaysTextField)
                    })
                    .rectangleForNextColumn(
                        RHSpacing.small,
                        autoExtendRuleWidths.endLabel
                    )
                    .assignedAsFrameOfView(this.autoExtendProcurementEndLabel)
                    .IF(this._templateLockButtons.deadlineExtension.superview === this.view)
                    .rectangleForNextColumn(RHSpacing.small, this._templateLockButtons.deadlineExtension.widthIncludingVisibilityButton)
                    .rectangleWithHeight(RHNewProcurementViewTokens.templateLockControl.size, 0.5)
                    .performingFunctionWithSelf(frame => this._templateLockButtons.deadlineExtension.layoutInFrame(frame))
                    .ENDIF()
            })

            .ENDIF()

            // Team and files
            .IF(this.templateManagersView.superview === this.view)
            .rectangleForNextRow(padding * 2, this.templateManagersView)
            .assignedAsFrameOfView(this.templateManagersView)
            .rectangleForNextRow(padding, this.showTeamInDerivedProcurementsCheckbox)
            .assignedAsFrameOfView(this.showTeamInDerivedProcurementsCheckbox)
            .ENDIF()
            .IF(this.procurementTeamView.superview === this.view)
            .rectangleForNextRow(padding * 2, this.procurementTeamView)
            .assignedAsFrameOfView(this.procurementTeamView)
            .ENDIF()
            .IF(this.fileUploadView.superview === this.view)
            .rectangleForNextRow(padding * 2, this.fileUploadSectionTitleLabel)
            .performingFunctionWithSelf(rowFrame =>
                this._templateLockButtons.files.layoutBesideTitleLabelInFrame(this.fileUploadSectionTitleLabel, rowFrame)
            )
            .rectangleForNextRow(RHProcurementWorkflowTitleTokens.contentTopGap, this.fileUploadView)
            .assignedAsFrameOfView(this.fileUploadView)
            .ENDIF()

            // Published-amendment explanation is deliberately the last input
            // before the required-fields legend and action buttons.
            .IF(this._isPublishedAmendmentMode)
            .rectangleForNextRow(padding * 2, this.amendmentSectionTitleLabel)
            .assignedAsFrameOfView(this.amendmentSectionTitleLabel)
            .rectangleForNextRow(padding, this.changeExplanationLabel)
            .assignedAsFrameOfView(this.changeExplanationLabel)
            .rectangleForNextRow(padding * 0.25, padding * 7)
            .assignedAsFrameOfView(this.changeExplanationTextArea)
            .ENDIF()
            .rectangleForNextRow(padding, this.requiredFieldsLegendLabel)
            .assignedAsFrameOfView(this.requiredFieldsLegendLabel)

            // Actions
            .rectangleForNextRow(padding, RHButtons.compactPrimaryHeight)
            .performingFunctionWithSelf(actionButtonRow => {
                actionButtonRow
                    .IF(this.cancelAmendmentButton.superview === this.view)
                    .rectangleWithMaxWidth(700, 0.5)
                    .distributeViewsEquallyAlongWidth(
                        [this.cancelAmendmentButton, this.nextButton],
                        padding
                    )
                    .ELSE_IF(this.saveButton.superview === this.view)
                    .rectangleWithMaxWidth(700, 0.5)
                    .distributeViewsEquallyAlongWidth(
                        [this.saveButton, this.nextButton],
                        padding
                    )
                    .ELSE()
                    .rectangleWithWidth(
                        [
                            [
                                this.nextButton.titleLabel.intrinsicContentWidth(RHButtons.compactPrimaryHeight)
                                + RHButtons.compactPrimaryContentPadding * 2
                                + RHSpacing.xLarge,
                                320
                            ].max(),
                            440
                        ].min(),
                        0.5
                    )
                    .assignedAsFrameOfView(this.nextButton)
                    .ENDIF()
            })

            // Footer clearance and backdrop
            .rectangleForNextRow(RHSpacing.large, 112)
            .assignedAsFrameOfView(this.footerClearanceView)
            .performingFunctionWithSelf(footerClearanceRow => {
                this.backdropView.weakFrame = bounds
                    .rectangleWithHeight(footerClearanceRow.max.y - bounds.y)
                    .rectangleWithInset(-RHSpacing.medium)
                this.backdropView.moveToBottomOfSuperview()
            })
    }


}
